<template>
  <div class="flex flex-col min-h-0">
    <div class="w-full mx-auto">
      <div class="relative flex flex-col min-h-0">
        <!-- No Teams Message -->
        <!--div
          v-if="!loading && (!leadedTeams || leadedTeams.length === 0)"
          class="flex-1 flex items-center justify-center"
        >
          <div class="text-center">
            <h2 class="text-gray-700 mb-2">
              {{ $t('TeamResultDashboardComponent.noTeamsAvailable') }}
            </h2>
            <p class="text-gray-500">
              {{ $t('TeamResultDashboardComponent.noTeamsMessage') }}
            </p>
          </div>
        </div-->

        <!-- Team Selector - Always visible -->
        <div class="mb-6 rounded-lg">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-4 flex-wrap">
              <h3 class="text-lg font-medium text-gray-700">
                {{ $t('TeamResultDashboardComponent.selectTeam') }}:
              </h3>
              <div class="relative inline-block max-w-full">
                <select
                  v-model="selectedTeamId"
                  @change="onTeamChange"
                  class="appearance-none bg-white border border-gray-300 rounded-lg px-4
                   py-2 pr-8 focus:outline-none focus:ring-2
                   focus:ring-orange-500 focus:border-transparent max-w-full min-w-0
                   [field-sizing:content] w-max"
                >
                  <option value="all">
                    {{ $t('TeamResultDashboardComponent.allTeams') }} ({{ teamsForClientDashboard.length }})
                  </option>
                  <option
                    v-for="team in teamsForClientDashboard"
                    :key="team._id"
                    :value="team._id"
                  >
                    {{ team.name }} ({{ team.members?.length || 0 }}
                     {{ $t('TeamResultDashboardComponent.members') }})
                  </option>
                </select>
                <div class="absolute inset-y-0 right-0 flex
                 items-center pr-2 pointer-events-none">
                  <svg class="w-4 h-4 text-gray-400" fill="none"
                   stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round"
                      stroke-width="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Assessment Type Dropdown (below team selector, when results exist) -->
        <div
          v-if="!loading && latestTeamAssessments?.length"
          class="mb-4 flex flex-wrap items-center gap-4"
        >
          <h3 class="text-lg font-medium text-gray-700">
            {{ $t('TeamResultDashboardComponent.selectAssessment') }}:
          </h3>
          <div class="relative inline-block max-w-full">
              <select
              :value="selectedTab"
              @change="changeTab($event.target.value)"
              class="appearance-none bg-white border border-gray-300 rounded-lg px-4
                   py-2 pr-8 focus:outline-none focus:ring-2
                   focus:ring-orange-500 focus:border-transparent max-w-full min-w-0
                   [field-sizing:content] w-max"
            >
            <option
              v-for="tab in latestTeamAssessments"
              :key="tab.assessmentId"
              :value="tab.assessmentId"
            >
              {{ getAssessmentName(tab.assessmentName) }}
            </option>
            </select>
                <div class="absolute inset-y-0 right-0 flex
                 items-center pr-2 pointer-events-none">
                  <svg class="w-4 h-4 text-gray-400" fill="none"
                   stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round"
                      stroke-width="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </div>
              </div>
        </div>

        <!-- No Results Message -->
        <div
          v-if="!loading && (!latestTeamAssessments || latestTeamAssessments.length === 0)"
          class="flex-1 flex items-center justify-center"
        >
          <div class="text-center">
            <h2 class="text-gray-700 mb-2">
              {{ hasAssessmentsButInsufficientParticipants ?
                 $t('TeamResultDashboardComponent.insufficientParticipants') :
                 $t('TeamResultDashboardComponent.noAssessmentResults') }}
            </h2>
            <p class="text-gray-500">
              {{ hasAssessmentsButInsufficientParticipants ?
                 $t('TeamResultDashboardComponent.needMinimumParticipants') :
                 $t('TeamResultDashboardComponent.noAssessmentsCompleted') }}
            </p>
            <p v-if="!hasAssessmentsButInsufficientParticipants" class="text-sm text-gray-400 mt-2">
              {{ $t('TeamResultDashboardComponent.needMinimumParticipants') }}
            </p>
          </div>
        </div>

        <!-- Assessment Type Tabs and Content -->
        <template v-else>
          <div class="min-h-0">
            <template v-for="tab in latestTeamAssessments" :key="tab.assessmentId">
              <div
                v-if="selectedTab === tab.assessmentId"
                class="block space-y-5"
              >
                <!-- Assessment title (separate from chart + comparison) -->
                <div class="bg-white p-6 rounded-lg shadow-md">
                  <div class="flex items-start gap-3 flex-wrap flex-col">
                    <div>
                      <h1 class="text-xl lg:text-2xl font-semibold">
                      {{ $t('TeamResultDashboardComponent.teamAssessmentResults',
                       { assessmentType: this.getAssessmentName(tab.assessmentName) }) }}
                    </h1>
                    </div>
                    <div class="flex flex-row gap-3">
                      <div class="flex items-center gap-4 text-sm text-gray-500 flex-wrap">
                        <span class="text-sm sm:text-base text-gray-500">
                          {{ $t('TeamResultDashboardComponent.submitted') }}:
                          {{ formatResultsDate(getDisplaySubmittedAtForTab(tab)) }}
                        </span>
                      </div>
                      <div class="text-sm sm:text-base text-gray-500">
                        {{ $t('TeamResultDashboardComponent.responsesOutOfMembers', {
                          completed: getParticipantCountForTab(tab),
                          total: currentScopeTeamMemberCount,
                        }) }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Chart, comparison controls, and related UI -->
                <div class="bg-white p-6 rounded-lg shadow-md flex flex-col flex-1 min-h-0">
                  <div class="flex gap-5 justify-start flex-1 min-h-0">
                    <div class="w-full">
                      <div class="flex flex-col min-h-0">
                        <div v-if="loading" class="flex justify-center items-center h-full">
                          <ProgressSpinner />
                        </div>
                        <div
                          v-else-if="error"
                          class="text-red-500 text-center p-4 h-full
                           flex items-center justify-center"
                        >
                          {{ error }}
                        </div>
                        <div
                          v-else-if="!tab.chartConfigs || tab.chartConfigs.length === 0"
                          class="text-center p-4 h-full flex items-center justify-center"
                        >
                          <div>
                            <h3 class="text-gray-700 mb-2">
                              {{ $t('TeamResultDashboardComponent.chartConfigurationMissing') }}
                            </h3>
                            <p class="text-gray-500">
                              {{ $t('TeamResultDashboardComponent.contactPlatformManager') }}
                            </p>
                          </div>
                        </div>
                        <div v-else class="min-h-0">
                          <!-- Charts (stacked) -->
                          <div class="flex mb-6 gap-3 flex-col space-y-8">
                            <div
                              v-for="(chartCfg, chartIdx) in tab.chartConfigs"
                              :key="chartCfg._id || chartIdx"
                              class="flex flex-col gap-3"
                            >
                              <!-- Time controls (MyResultsDashboardComponent UI); one block per line chart -->
                              <div
                                v-if="chartCfg.type === 'line'"
                                class="mb-4 flex flex-col sm:flex-row
                                  items-start sm:items-center
                                  justify-center gap-3 sm:gap-6"
                              >
                                <div class="flex items-center gap-2">
                                  <span class="text-base sm:text-lg font-medium text-gray-700">
                                    {{ $t('TimePeriodSelector.timePeriod')
                                      || 'Time Period' }}:
                                  </span>
                                  <Select
                                    :modelValue="selectedTimePeriod"
                                    @update:modelValue="(v) => onTimePeriodChange(v)"
                                    :options="timePeriodOptions"
                                    optionLabel="label"
                                    optionValue="value"
                                    class="w-fit max-w-full min-w-0"
                                  />
                                </div>
                                <div class="flex items-center gap-2">
                                  <span class="text-base sm:text-lg font-medium text-gray-700">
                                    {{ $t(
                                      'MyResultsDashboardComponent.timeGranularity'
                                    ) || 'Interval' }}:
                                  </span>
                                  <Select
                                    v-model="selectedTimeGranularity"
                                    :options="granularityOptions"
                                    optionLabel="label"
                                    optionValue="value"
                                    @change="onTimeGranularityChange"
                                    class="w-fit max-w-full min-w-0"
                                  />
                                </div>
                              </div>

                              <div
                                v-if="chartCfg.type === 'line'
                                  && selectedTimePeriod === 'custom'"
                                class="mb-4 flex flex-col sm:flex-row items-center
                                  justify-center gap-3 p-3 bg-gray-50 rounded-lg"
                              >
                                <div class="flex items-center gap-2">
                                  <label class="text-base sm:text-lg font-medium text-gray-700">
                                    {{ $t('TimePeriodSelector.fromDate')
                                      || 'From:' }}
                                  </label>
                                  <input
                                    v-model="customStartDate"
                                    type="date"
                                    class="px-2 py-1.5 border border-gray-300
                                      rounded-md text-sm sm:text-base focus:outline-none
                                      focus:ring-2 focus:ring-orange-500
                                      focus:border-orange-500"
                                    @change="onCustomDateChange"
                                  />
                                </div>
                                <div class="flex items-center gap-2">
                                  <label class="text-base sm:text-lg font-medium text-gray-700">
                                    {{ $t('TimePeriodSelector.toDate') || 'To:' }}
                                  </label>
                                  <input
                                    v-model="customEndDate"
                                    type="date"
                                    class="px-2 py-1.5 border border-gray-300
                                      rounded-md text-sm sm:text-base focus:outline-none
                                      focus:ring-2 focus:ring-orange-500
                                      focus:border-orange-500"
                                    @change="onCustomDateChange"
                                  />
                                </div>
                                <button
                                  @click="applyCustomRange"
                                  class="px-3 py-1.5 bg-orange-500 text-white
                                    rounded-md text-sm sm:text-base font-medium
                                    hover:bg-orange-600 transition-colors"
                                >
                                  {{ $t('TimePeriodSelector.apply') || 'Apply' }}
                                </button>
                              </div>

                              <!-- Comparison (ResultsComparisonSection — same as own results) -->
                              <ResultsComparisonSection
                                v-if="
                                  groupedTeamAssessments[tab.assessmentType]?.length >= 1 &&
                                  isBarComparisonMode(tab, chartIdx)
                                "
                                class="mb-4 sm:mb-5"
                                :currentAssessmentId="tab.assessmentId"
                                :comparison1="
                                  selectedComparison1[tab.assessmentId] || ''"
                                :comparison2="
                                  selectedComparison2[tab.assessmentId] || ''"
                                :sameTypeAssessments="
                                  teamSameTypeAssessmentsForComparison(tab)"
                                :externalEvaluations="[]"
                                :isExternalEvaluationTab="false"
                                :deltas="getTeamComparisonDeltas(tab)"
                                :getAssessmentName="getAssessmentName"
                                @update:comparison1="
                                  onComparison1Update(tab, $event)"
                                @update:comparison2="
                                  onComparison2Update(tab, $event)"
                                @reset="resetComparison(tab.assessmentId)"
                              />

                              <p
                                v-if="getChartDescription(chartCfg)"
                                class="text-sm sm:text-base text-gray-600 mb-3 text-center"
                              >
                                {{ getChartDescription(chartCfg) }}
                              </p>
                              <div class="flex items-start justify-start w-full" :class="chartHeightClass(chartCfg)">
                                <BoxPlotComponent
                                  v-if="chartCfg && chartCfg.type === 'boxplot'"
                                  :chart-config="chartCfg"
                                  :dimensions="dimensions"
                                  :tooltip-footer-text="getTeamBoxplotTooltipFooterText(tab)"
                                />
                                <BarChartComponent
                                  v-else-if="chartCfg && (chartCfg.type === 'bar' || chartCfg.type === 'barWithErrorBars')"
                                  :key="`bar-${tab.assessmentId}-${chartIdx}-${chartResetKeys[tab.assessmentId] || 0}`"
                                  :chart-config="chartCfg"
                                  :dimensions="dimensions"
                                />
                                <LineChartComponent
                                  v-else-if="chartCfg && chartCfg.type === 'line'"
                                  :chart-config="chartCfg"
                                  :dimensions="dimensions"
                                />
                              </div>
                              <div class="flex items-center justify-center">
                                <p
                                  v-if="getDataReliabilityMessageKey(tab)"
                                  class="text-xs text-gray-500 mt-0.5 max-w-2xl"
                                >
                                  {{ $t(getDataReliabilityMessageKey(tab)) }}
                                </p>
                              </div>

                              <!-- Standard Deviation Info for Bar Charts -->
                              <div
                                v-if="
                                  (selectedComparison1[tab.assessmentId]
                                  || selectedComparison2[tab.assessmentId]) &&
                                  (chartCfg.type === 'bar' || chartCfg.type === 'barWithErrorBars')
                                "
                                class="w-full border-t pt-4"
                              >
                                <div class="flex items-center justify-center
                                  gap-2 text-sm text-gray-600">
                                  <div class="w-4 h-3 bg-black rounded-sm"></div>
                                  <span>
                                    {{ $t('TeamResultDashboardComponent.standardDeviationBars')
                                      || 'Black bars show standard deviation for each dimension' }}
                                  </span>
                                </div>
                              </div>

                              <div
                                v-if="chartCfg?.type === 'boxplot'"
                                class="flex flex-col sm:flex-row gap-2
                                  text-sm sm:text-base text-gray-400 mt-2"
                              >
                                <div>
                                  <p class="text-gray-600">
                                    {{ $t('MyResultsDashboardComponent.boxPlotDepictionDistribution') }}:</p>
                                  <ul class="list-disc list-inside">
                                    <li>{{ $t('MyResultsDashboardComponent.depiction1') }}</li>
                                    <li>{{ $t('MyResultsDashboardComponent.depiction2') }}</li>
                                    <li>{{ $t('MyResultsDashboardComponent.depiction3') }}</li>
                                    <li>{{ $t('MyResultsDashboardComponent.depiction4') }}</li>
                                    <li>{{ $t('MyResultsDashboardComponent.depiction5') }}</li>
                                  </ul>
                                </div>
                                <div>
                                  <p class="text-gray-600">
                                    {{ $t('MyResultsDashboardComponent.boxPlotGraphInterpretation') }}:</p>
                                  <ul class="list-disc list-inside">
                                    <li>{{ $t('MyResultsDashboardComponent.interpretation1') }}</li>
                                    <li>{{ $t('MyResultsDashboardComponent.interpretation2') }}</li>
                                    <li>{{ $t('MyResultsDashboardComponent.interpretation3') }}</li>
                                  </ul>
                                </div>
                              </div>

                              <BoxPlotStatsSection
                                v-if="chartCfg?.type === 'boxplot'"
                                :rows="getTeamBoxplotStatsRowsForTab(tab, chartCfg)"
                                :series-caption="getTeamBoxplotStatsSeriesCaption(tab, chartCfg)"
                              />
                            </div>
                            <LikertScaleLegend
                              v-if="tabHasLikertScaleChart(tab)"
                              variant="sectionEnd"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </template>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import BarChartComponent from './BarChartComponent.vue';
import LineChartComponent from './LineChartComponent.vue';
import BoxPlotComponent from './BoxPlotComponent.vue';
import BoxPlotStatsSection from './BoxPlotStatsSection.vue';
import LikertScaleLegend from './LikertScaleLegend.vue';
import ResultsComparisonSection from './ResultsComparisonSection.vue';
import ProgressSpinner from 'primevue/progressspinner';
import Select from 'primevue/select';
import { mapState, mapActions } from 'vuex';
import { formatLocaleDate } from '@/helpers/formatLocaleDate';
import { roundToTwoDecimals } from '@/helpers/formatOwnResultNumber';
import {
  dimensionScoresFromRangeSliderQuestions,
  rangeSliderMeanForDimension,
} from '@/helpers/assessmentDimensionScore';
import { withBoxplotExactWhiskers, clampLikertBoxplotStat } from '@/helpers/boxplotStats';
import {
  buildBoxplotStatsRows,
  getBoxplotPrimaryDatasetIndex,
} from '@/helpers/boxplotStatsTable';

export default {
  name: 'TeamResultDashboardComponent',
  components: {
    BarChartComponent,
    LineChartComponent,
    BoxPlotComponent,
    BoxPlotStatsSection,
    LikertScaleLegend,
    ResultsComparisonSection,
    ProgressSpinner,
    Select,
  },
  data() {
    return {
      selectedTab: null,
      selectedTeamId: 'all',
      selectedTimePeriod: 'all', // Time period filter for line charts
      customDateRange: null, // Store custom date range for filtering
      customStartDate: '',
      customEndDate: '',
      selectedTimeGranularity: 'weekly', // Daily, Weekly, Monthly
      selectedComparison1: {}, // First comparison selection
      selectedComparison2: {}, // Second comparison selection
      loading: true,
      error: null,
      rawTeamAssessments: [], // Store raw individual assessments for line charts
      groupedTeamAssessments: {}, // Group assessments by type for comparisons
      originalChartConfigs: {}, // Store original chart configs for recreation
      latestOnlyChartConfigs: {}, // Immutable copy of each assessment's chart (tab is mutated during comparisons)
      chartResetKeys: {}, // Bump on reset so bar chart re-mounts with latest-only config
      // Configurable constants
      config: {
        chartScale: {
          min: 0,
          max: 5,
        },
      },
    };
  },
  computed: {
    ...mapState('assessments', ['teamAssessments', 'minOfDatasetsForAggResults']),
    ...mapState('assessmentSchemas', ['assessmentSchemas']),
    ...mapState('charts', ['charts']),
    ...mapState('dimensions', ['dimensions']),

    tmLeadedTeams() {
      return this.$store.state.teamManagement.tmLeadedTeams;
    },

    leadedTeams() {
      return this.tmLeadedTeams;
    },

    /** Teams included in client-facing dashboard aggregates (excludes admin/internal teams). */
    teamsForClientDashboard() {
      return (this.leadedTeams || []).filter((t) => t.isAdminTeam !== true);
    },

    // Get minParticipants from store (fetched from backend config)
    minParticipants() {
      return this.minOfDatasetsForAggResults || 5;
    },

    /** Team members in selected scope (all teams or one team), for response/team ratio. */
    currentScopeTeamMemberCount() {
      if (!this.leadedTeams?.length) return 0;
      if (this.selectedTeamId === 'all') {
        const filteredTeams = this.teamsForClientDashboard;
        const all = filteredTeams.flatMap((t) => t.members || []);
        return [...new Set(all)].length;
      }
      const team = this.leadedTeams.find((t) => t._id === this.selectedTeamId);
      if (!team?.members) return 0;
      return [...new Set(team.members)].length;
    },

    latestTeamAssessments() {
      if (!this.teamAssessments || this.teamAssessments.length === 0) return [];

      // Group assessments by type
      const groupedAssessments = this.teamAssessments.reduce((acc, assessment) => {
        if (!acc[assessment.assessmentType]) {
          acc[assessment.assessmentType] = [];
        }
        acc[assessment.assessmentType].push(assessment);
        return acc;
      }, {});

      // Get latest assessment for each type
      return Object.values(groupedAssessments).map((assessments) => {
        const latest = assessments.sort(
          (a, b) => new Date(b.submittedAt) - new Date(a.submittedAt),
        )[0];
        return latest;
      });
    },

    hasAssessmentsButInsufficientParticipants() {
      // Check if there are team assessments but don't meet the minimum participants requirement
      if (!this.teamAssessments || this.teamAssessments.length === 0) return false;

      // Group assessments by type and count unique users
      const assessmentsByType = {};
      this.teamAssessments.forEach((assessment) => {
        const { type, assigned_to: userId } = assessment;
        if (!assessmentsByType[type]) {
          assessmentsByType[type] = new Set();
        }
        assessmentsByType[type].add(userId);
      });

      // Check if any assessment type has fewer users than the minimum required
      return Object.values(assessmentsByType).some(
        (userSet) => userSet.size < this.minParticipants,
      );
    },

    /** PrimeVue Select options — match MyResultsDashboardComponent */
    timePeriodOptions() {
      return [
        { value: 'all', label: this.$t('TimePeriodSelector.allTime') || 'All Time' },
        { value: 'week', label: this.$t('TimePeriodSelector.lastWeek') || 'Last Week' },
        { value: 'month', label: this.$t('TimePeriodSelector.lastMonth') || 'Last Month' },
        { value: 'quarter', label: this.$t('TimePeriodSelector.lastQuarter') || 'Last Quarter' },
        { value: 'year', label: this.$t('TimePeriodSelector.lastYear') || 'Last Year' },
        { value: 'custom', label: this.$t('TimePeriodSelector.customRange') || 'Custom Range' },
      ];
    },

    granularityOptions() {
      return [
        { value: 'daily', label: this.$t('MyResultsDashboardComponent.daily') || 'Daily' },
        { value: 'weekly', label: this.$t('MyResultsDashboardComponent.weekly') || 'Weekly' },
        { value: 'monthly', label: this.$t('MyResultsDashboardComponent.monthly') || 'Monthly' },
      ];
    },
  },
  methods: {
    ...mapActions('teamManagement', ['getLeadedTeams']),
    ...mapActions('assessments', ['getTeamAssessmentsResults']),
    ...mapActions('assessmentSchemas', ['fetchAssessments']),
    ...mapActions('charts', ['fetchCharts']),
    ...mapActions('dimensions', ['fetchDimensions']),

    getChartDescription(chartCfg) {
      if (!chartCfg?.description) return '';
      const locale = this.$i18n?.locale || 'en';
      const txt = chartCfg.description?.[locale];
      return typeof txt === 'string' ? txt.trim() : '';
    },

    formatResultsDate(value) {
      return formatLocaleDate(value, this.$i18n?.locale);
    },

    /** Responsive chart height class (matches MyResultsDashboardComponent). */
    chartHeightClass(chartConfig) {
      if (!chartConfig) return 'h-[300px] lg:h-[400px]';
      return 'h-[300px] lg:h-[400px]';
    },

    isLikertScaleChartConfig(chartCfg) {
      const y = chartCfg?.options?.scales?.y;
      if (!y || y.min !== 0) return false;
      const max = y.max ?? y.suggestedMax;
      return max === 5 || max === 6;
    },

    tabHasLikertScaleChart(tab) {
      const configs = tab?.chartConfigs?.length
        ? tab.chartConfigs
        : (tab?.chartConfig ? [tab.chartConfig] : []);
      return configs.some((cfg) => this.isLikertScaleChartConfig(cfg));
    },

    /**
     * Date used to bucket an assessment into a period (same order as processTeamAssessmentData).
     */
    getRawAssessmentPeriodDate(a) {
      return new Date(
        a.date_taskBegin
          || a.date_assessmentMade
          || a.submittedAt
          || a.date
          || a.createdAt
          || 0,
      );
    },

    /**
     * Same “when” field order as line chart grouping / time filters (updateChartData).
     */
    getChartSubmissionTimestamp(a) {
      const t = new Date(
        a.date_assessmentMade
          || a.submittedAt
          || a.date
          || a.createdAt
          || 0,
      ).getTime();
      return Number.isFinite(t) && t > 0 ? t : NaN;
    },

    /**
     * Raw assessments for a line chart tab (type filter only — no time-period filter).
     * Used for header stats that must reflect the latest cohort, not the chart’s time range.
     */
    getLineChartAssessmentsForTabUnfiltered(tab) {
      if (!tab?.assessmentType) return [];
      let allAssessments = [];
      if (this.rawTeamAssessments?.length) {
        allAssessments = this.rawTeamAssessments
          .filter((assessment) => assessment.type === tab.assessmentType)
          .slice();
      }
      if (allAssessments.length === 0) {
        allAssessments = (this.$store.state.assessments.teamAssessments || [])
          .filter((assessment) => assessment.assessmentType === tab.assessmentType
            && !assessment.isTeamAverage)
          .slice();
      }
      return allAssessments;
    },

    /**
     * Raw assessments for a line chart tab — same pipeline as updateChartData (type filter + time filter).
     */
    getLineChartAssessmentsForTab(tab) {
      return this.filterAssessmentsByTimePeriod(this.getLineChartAssessmentsForTabUnfiltered(tab));
    },

    /**
     * Same validity rules as processTeamAssessmentData for a bucket of raw assessments.
     */
    filterValidTeamAssessmentsForLineBucket(assessments) {
      if (!assessments?.length) return [];
      const firstAssessment = assessments[0];
      if (firstAssessment.results?.dimensions
        && Object.keys(firstAssessment.results.dimensions).length > 0) {
        return assessments.filter((a) => a.results && a.results.dimensions);
      }
      if (this.usesQuestionArrayStructure(firstAssessment)) {
        return assessments.filter((a) => a._id && this.usesQuestionArrayStructure(a));
      }
      return assessments.filter((a) => a.results && a.results.dimensions);
    },

    /**
     * Responses count for the header: bar/boxplot uses processed period count; line chart uses
     * the latest time bucket over full history (ignores line-chart time-period filter).
     */
    getParticipantCountForTab(tab) {
      if (!tab) return 0;
      if (!this.teamLineChartConfig(tab)) {
        return tab.participantCount ?? 0;
      }
      const raw = this.getLineChartAssessmentsForTabUnfiltered(tab);
      if (!raw.length) return tab.participantCount ?? 0;
      const grouped = this.groupAssessmentsByTimePeriod(raw, this.selectedTimeGranularity);
      const keys = Object.keys(grouped).sort();
      if (!keys.length) return tab.participantCount ?? 0;
      const lastKey = keys[keys.length - 1];
      const bucket = grouped[lastKey] || [];
      return this.filterValidTeamAssessmentsForLineBucket(bucket).length;
    },

    /**
     * Raw assessments for a processed period (bar / boxplot / same monthly bucket as processTeamAssessmentData).
     * @param {{ assessmentType: string, periodKey?: string, assessmentId?: string }} assessment
     */
    getBarBoxplotPeriodAssessmentsForTab(assessment) {
      if (!assessment?.assessmentType || !this.rawTeamAssessments?.length) return [];
      const periodKey = assessment.periodKey
        || (typeof assessment.assessmentId === 'string'
          ? assessment.assessmentId.match(/(\d{4}-\d{2}-\d{2})$/)?.[1]
          : null);
      if (!periodKey) return [];

      const periodStart = new Date(periodKey);
      periodStart.setHours(0, 0, 0, 0);
      const periodEnd = new Date(periodStart);
      periodEnd.setMonth(periodEnd.getMonth() + 1);

      return this.rawTeamAssessments.filter((a) => {
        if (a.type !== assessment.assessmentType) return false;
        const d = this.getRawAssessmentPeriodDate(a);
        return d >= periodStart && d < periodEnd;
      });
    },

    /**
     * Latest submission in that period (same fields as line grouping) — for header, comparison dropdown, legend.
     */
    getPeriodLatestSubmissionDateForAssessment(assessment) {
      if (!assessment?.assessmentType) return assessment?.submittedAt ?? null;
      const list = this.getBarBoxplotPeriodAssessmentsForTab(assessment);
      if (!list.length) return assessment.submittedAt ?? null;

      let maxTs = -Infinity;
      list.forEach((a) => {
        const t = this.getChartSubmissionTimestamp(a);
        if (Number.isFinite(t) && t > maxTs) maxTs = t;
      });
      if (maxTs === -Infinity) return assessment.submittedAt ?? null;
      return new Date(maxTs).toISOString();
    },

    /**
     * Rightmost line-chart x bucket over full history (same keys as updateChartData with “all time”).
     * Ignores the line-chart time-period filter so the header date matches participant count.
     */
    getLineChartLastTimeKey(tab) {
      const raw = this.getLineChartAssessmentsForTabUnfiltered(tab);
      if (!raw.length) return null;
      const grouped = this.groupAssessmentsByTimePeriod(raw, this.selectedTimeGranularity);
      const keys = Object.keys(grouped).sort();
      if (!keys.length) return null;
      return keys[keys.length - 1];
    },

    getDisplaySubmittedAtForTab(tab) {
      if (!tab) return null;
      if (this.teamLineChartConfig(tab)) {
        const lastKey = this.getLineChartLastTimeKey(tab);
        if (lastKey) {
          const d = new Date(lastKey);
          if (!Number.isNaN(d.getTime())) return d.toISOString();
        }
        return tab.submittedAt ?? null;
      }
      return this.getPeriodLatestSubmissionDateForAssessment(tab);
    },

    /**
     * i18n key for data-basis hint (participation vs team size). Null if no team denominator.
     */
    getDataReliabilityMessageKey(tab) {
      const completed = this.getParticipantCountForTab(tab);
      const total = this.currentScopeTeamMemberCount;
      if (total <= 0) return null;
      const p = completed / total;
      if (p <= 0.25) return 'TeamResultDashboardComponent.dataReliabilityLow';
      if (p <= 0.50) return 'TeamResultDashboardComponent.dataReliabilityMedium';
      return 'TeamResultDashboardComponent.dataReliabilityHigh';
    },

    getTeamBoxplotTooltipFooterText(tab) {
      const key = this.getDataReliabilityMessageKey(tab);
      return key ? this.$t(key) : '';
    },

    calculateStandardDeviation(values) {
      const n = values.length;
      if (n === 0) return 0;

      const mean = values.reduce((sum, val) => sum + val, 0) / n;
      const squaredDiffs = values.map((val) => (val - mean) ** 2);
      const variance = squaredDiffs.reduce((sum, val) => sum + val, 0) / n;
      return Math.sqrt(variance);
    },

    /** Quartiles for team bar→boxplot (values per dimension across periods). */
    computeQuartilesForTeam(values) {
      if (!values || values.length === 0) return null;
      const sorted = [...values].filter((v) => v != null && !Number.isNaN(v)).sort((a, b) => a - b);
      const n = sorted.length;
      if (n === 0) return null;
      const min = sorted[0];
      const max = sorted[n - 1];
      const q1Idx = (n - 1) * 0.25;
      const medIdx = (n - 1) * 0.5;
      const q3Idx = (n - 1) * 0.75;
      const lerp = (arr, i) => {
        const lo = Math.floor(i);
        const hi = Math.min(lo + 1, n - 1);
        const t = i - lo;
        return arr[lo] * (1 - t) + arr[hi] * t;
      };
      return {
        min,
        q1: lerp(sorted, q1Idx),
        median: lerp(sorted, medIdx),
        q3: lerp(sorted, q3Idx),
        max,
      };
    },

    /**
     * Boxplot stats from per-member scores on one dimension (spread across people).
     * Falls back is not used here — see buildBoxplotRowFromAssessment.
     */
    boxplotStatsFromMemberValues(values) {
      const nums = [...values]
        .filter((v) => v != null && !Number.isNaN(Number(v)))
        .map((v) => Number(v));
      if (nums.length === 0) return null;
      const mean = nums.reduce((s, v) => s + v, 0) / nums.length;
      const sd = this.calculateStandardDeviation(nums);
      if (nums.length === 1) {
        const v = nums[0];
        return withBoxplotExactWhiskers({
          min: v,
          q1: v,
          median: v,
          q3: v,
          max: v,
          mean: v,
          sd: 0,
        });
      }
      const q = this.computeQuartilesForTeam(nums);
      if (!q) return null;
      return withBoxplotExactWhiskers({
        min: clampLikertBoxplotStat(q.min),
        q1: clampLikertBoxplotStat(q.q1),
        median: q.median,
        q3: Math.min(5, q.q3),
        max: Math.min(5, q.max),
        mean,
        sd,
      });
    },

    teamChartConfigAt(assessment, chartIndex = 0) {
      if (!assessment) return null;
      return assessment.chartConfigs?.[chartIndex] ?? assessment.chartConfig;
    },

    teamLineChartConfig(tab) {
      return tab?.chartConfigs?.find((c) => c.type === 'line') || null;
    },

    shouldRenderTeamBarChartAsBoxplot(tab, chartIndex = 0) {
      const stored = this.originalChartConfigs[tab.assessmentType];
      const orig = stored?.chartConfigs?.[chartIndex] ?? stored?.chartConfig;
      const vis = orig?.visibility ?? 'both';
      if (!['team', 'both'].includes(vis)) return false;
      const bb = orig?.barBoxplotWhenAggregated;
      if (!bb?.enabled || !bb.teamResults) return false;
      const min = this.minParticipants;
      // Unlike own results, groupedTeamAssessments[type] is one entry per *time period*
      // (e.g. monthly bucket), not one per assessment — comparing its length to min
      // would almost never pass. Use this period's team member count instead (same
      // threshold already enforced in processTeamAssessmentData).
      const n = tab.participantCount ?? 0;
      return n >= min;
    },

    isBarComparisonMode(tab, chartIndex = 0) {
      const stored = this.originalChartConfigs[tab.assessmentType];
      const originalType = stored?.chartConfigs?.[chartIndex]?.type ?? stored?.chartConfig?.type;
      const currentType = this.teamChartConfigAt(tab, chartIndex)?.type;
      return originalType === 'bar' || currentType === 'bar';
    },

    normalizeColor(color, fallback = '#000000') {
      if (!color) return fallback;
      if (typeof color === 'string' && (color.startsWith('#') || color.startsWith('rgb'))) {
        return color;
      }
      return `#${String(color).replace(/^#/, '')}`;
    },

    getBaseDatasetForAssessment(assessment, chartIndex = 0) {
      const saved = this.latestOnlyChartConfigs[assessment.assessmentId];
      if (Array.isArray(saved)) {
        const s = saved[chartIndex];
        if (s?.data?.datasets?.[0]) return s.data.datasets[0];
      } else if (saved?.data?.datasets?.[0] && chartIndex === 0) {
        return saved.data.datasets[0];
      }
      return this.teamChartConfigAt(assessment, chartIndex)?.data?.datasets?.[0] || null;
    },

    resolveDimensionKeyForLabel(assessment, label, idx, fallbackDimensionKeys = []) {
      const stdDevByDim = assessment?.standardDeviation?.dimensions || {};
      const resultDims = assessment?.results?.dimensions || {};
      const stdKeys = Object.keys(stdDevByDim);
      const resultKeys = Object.keys(resultDims);
      const candidateKeys = stdKeys.length ? stdKeys : resultKeys;

      // 1) direct key match
      if (candidateKeys.includes(label)) return label;

      // 2) translated-label match
      const translatedMatch = candidateKeys.find((k) => this.getDimensionLabel(k) === label);
      if (translatedMatch) return translatedMatch;

      // 3) chart dimension-order fallback
      if (fallbackDimensionKeys[idx]) return fallbackDimensionKeys[idx];

      // 4) candidate-order fallback
      if (candidateKeys[idx]) return candidateKeys[idx];

      return null;
    },

    buildBoxplotRowFromAssessment(assessment, labels, fallbackDimensionKeys = [], chartIndex = 0) {
      const baseDataset = this.getBaseDatasetForAssessment(assessment, chartIndex);
      const barValues = baseDataset?.data || [];
      const stdDevByDim = assessment?.standardDeviation?.dimensions || {};
      const memberByDim = assessment?.memberDimensionValues || {};
      return labels.map((label, idx) => {
        const mean = barValues[idx];
        const dimensionKey = this.resolveDimensionKeyForLabel(
          assessment,
          label,
          idx,
          fallbackDimensionKeys,
        );

        const memberVals = dimensionKey ? memberByDim[dimensionKey] : null;
        if (memberVals && memberVals.length > 0) {
          const fromMembers = this.boxplotStatsFromMemberValues(memberVals);
          if (fromMembers) return fromMembers;
        }

        if (typeof mean !== 'number' || Number.isNaN(mean)) return null;
        const sd = dimensionKey && typeof stdDevByDim[dimensionKey] === 'number'
          ? stdDevByDim[dimensionKey]
          : 0;
        return withBoxplotExactWhiskers({
          min: clampLikertBoxplotStat(mean - sd),
          q1: clampLikertBoxplotStat(mean - 0.67 * sd),
          median: mean,
          q3: Math.min(5, mean + 0.67 * sd),
          max: Math.min(5, mean + sd),
          mean,
          sd,
        });
      });
    },

    buildTeamAggregatedBoxplotConfig(tab, chartIndex = 0) {
      const cfg = this.teamChartConfigAt(tab, chartIndex);
      const labels = cfg?.data?.labels;
      if (!labels?.length) return null;
      const stored = this.originalChartConfigs[tab.assessmentType];
      const sourceConfig = (stored?.chartConfigs?.[chartIndex] ?? stored?.chartConfig) || cfg;
      const fallbackDimensionKeys = sourceConfig?.dimensions || [];

      const assessmentId = tab.assessmentId;
      const hasComparison1 = !!this.selectedComparison1[assessmentId];
      const hasComparison2 = !!this.selectedComparison2[assessmentId];
      const sourceAssessment = this.groupedTeamAssessments[tab.assessmentType]?.find(
        (a) => a.assessmentId === assessmentId,
      );
      if (!sourceAssessment) return null;

      const sourceDataset = this.getBaseDatasetForAssessment(sourceAssessment, chartIndex);
      const originalStored = this.originalChartConfigs[tab.assessmentType];
      const baseChartConfig = originalStored?.chartConfigs?.[chartIndex] || originalStored?.chartConfig
        ? JSON.parse(JSON.stringify(
          originalStored.chartConfigs?.[chartIndex] ?? originalStored.chartConfig,
        ))
        : null;
      const colorSource = baseChartConfig?.data?.datasets?.[0] || sourceDataset;
      const barChartConfigColor = (() => {
        const bg = colorSource?.backgroundColor;
        const bc = colorSource?.borderColor;
        const color = (typeof bg === 'string' ? bg : bg?.[0]) || (typeof bc === 'string' ? bc : bc?.[0]);
        return this.normalizeColor(color, '#f97316');
      })();
      const barChartGreyColor = '#9ca3af';
      const accentBorderColor = this.normalizeColor(sourceDataset?.borderColor, '#000000');
      const greyBorderColor = '#000000';

      const getCompareDateLabel = (a) => {
        const iso = this.getPeriodLatestSubmissionDateForAssessment(a);
        return iso ? formatLocaleDate(iso, this.$i18n?.locale) : '';
      };
      const datasets = [];
      const pushDataset = (assessment, bgColor, borderColor) => {
        const row = this.buildBoxplotRowFromAssessment(
          assessment,
          labels,
          fallbackDimensionKeys,
          chartIndex,
        );
        if (row.every((x) => x == null)) return;
        datasets.push({
          label: `${this.getAssessmentName(assessment.assessmentName)} ${getCompareDateLabel(assessment)}`.trim(),
          data: row,
          backgroundColor: bgColor,
          borderColor,
          borderWidth: 1,
        });
      };

      if (!hasComparison1 && !hasComparison2) {
        pushDataset(
          sourceAssessment,
          sourceDataset?.backgroundColor || 'rgba(200, 58, 121, 0.7)',
          accentBorderColor,
        );
      } else if (hasComparison1 && !hasComparison2) {
        const left = this.groupedTeamAssessments[tab.assessmentType]?.find(
          (a) => a.assessmentId === this.selectedComparison1[assessmentId],
        );
        if (left) pushDataset(left, barChartGreyColor, greyBorderColor);
        pushDataset(sourceAssessment, barChartConfigColor, accentBorderColor);
      } else if (!hasComparison1 && hasComparison2) {
        pushDataset(sourceAssessment, barChartGreyColor, greyBorderColor);
        const right = this.groupedTeamAssessments[tab.assessmentType]?.find(
          (a) => a.assessmentId === this.selectedComparison2[assessmentId],
        );
        if (right) pushDataset(right, barChartConfigColor, accentBorderColor);
      } else {
        const left = this.groupedTeamAssessments[tab.assessmentType]?.find(
          (a) => a.assessmentId === this.selectedComparison1[assessmentId],
        );
        const right = this.groupedTeamAssessments[tab.assessmentType]?.find(
          (a) => a.assessmentId === this.selectedComparison2[assessmentId],
        );
        if (left) pushDataset(left, barChartGreyColor, greyBorderColor);
        if (right) pushDataset(right, barChartConfigColor, accentBorderColor);
      }

      if (datasets.length === 0) return null;

      return {
        type: 'boxplot',
        data: { labels, datasets },
        options: {
          plugins: { legend: { display: datasets.length > 1 } },
          scales: { y: { min: 0, max: 5 } },
        },
      };
    },

    getTeamBoxplotStatsRowsForTab(tab, chartCfg) {
      const cfg = chartCfg || tab?.chartConfig;
      if (cfg?.type !== 'boxplot') return [];
      const labels = cfg.data?.labels || [];
      const ds = cfg.data?.datasets || [];
      if (!labels.length || !ds.length) return [];
      const id = tab.assessmentId;
      const idx = getBoxplotPrimaryDatasetIndex(
        !!(id && this.selectedComparison1[id]),
        !!(id && this.selectedComparison2[id]),
        ds.length,
      );
      const dataset = ds[idx];
      return buildBoxplotStatsRows(
        labels,
        dataset?.data || [],
        (l) => this.getDimensionLabel(l),
      );
    },

    getTeamBoxplotStatsSeriesCaption(tab, chartCfg) {
      const cfg = chartCfg || tab?.chartConfig;
      if (cfg?.type !== 'boxplot') return '';
      const ds = cfg.data?.datasets || [];
      if (ds.length <= 1) return '';
      const id = tab.assessmentId;
      const idx = getBoxplotPrimaryDatasetIndex(
        !!(id && this.selectedComparison1[id]),
        !!(id && this.selectedComparison2[id]),
        ds.length,
      );
      const label = ds[idx]?.label;
      return label
        ? `${this.$t('BoxPlotStatsSection.seriesValuesFor')}: ${label}`
        : '';
    },

    updateBarChartWithComparisons(assessmentId) {
      const tab = this.latestTeamAssessments.find((t) => t.assessmentId === assessmentId);
      if (!tab) {
        return;
      }
      if (!tab.chartConfigs?.length) {
        if (!tab.chartConfig) return;
        tab.chartConfigs = [tab.chartConfig];
      }

      for (let chartIndex = 0; chartIndex < tab.chartConfigs.length; chartIndex += 1) {
        tab.chartConfig = tab.chartConfigs[chartIndex];
        if (tab.chartConfig.type !== 'bar' && tab.chartConfig.type !== 'barWithErrorBars') {
          continue;
        }

      // Configure stacking based on whether comparisons are being made
      const hasComparison1 = this.selectedComparison1[assessmentId];
      const hasComparison2 = this.selectedComparison2[assessmentId];
      const hasComparisons = hasComparison1 || hasComparison2;

      // Create a copy of the current chart config; use original for latest series and color so they are not lost after comparison runs
      const updatedChartConfig = JSON.parse(JSON.stringify(tab.chartConfig));
      const datasets = [];

      // Assessment that matches this tab (latest period) – use its chart config for correct bar data and for reset
      const sourceAssessment = this.groupedTeamAssessments[tab.assessmentType]?.find(
        (a) => a.assessmentId === tab.assessmentId,
      );
      // Latest series: use dataset from tab's assessment so bar values are correct (not from template)
      const latestDatasetSource = this.teamChartConfigAt(sourceAssessment, chartIndex)?.data?.datasets?.[0]
        ?? updatedChartConfig.data.datasets[0];
      // Config color from template or from latest dataset
      const originalStored = this.originalChartConfigs[tab.assessmentType];
      const baseChartConfig = (originalStored?.chartConfigs?.[chartIndex] || originalStored?.chartConfig)
        ? JSON.parse(JSON.stringify(
          originalStored.chartConfigs?.[chartIndex] ?? originalStored.chartConfig,
        ))
        : null;
      const colorSource = baseChartConfig?.data?.datasets?.[0] || latestDatasetSource;

      // Case 1: No comparisons - show only the latest period (restore from immutable copy; tab/sourceAssessment are same ref and get mutated during comparisons)
      if (!hasComparison1 && !hasComparison2) {
        const saved = this.latestOnlyChartConfigs[tab.assessmentId];
        let restored = null;
        if (Array.isArray(saved)) {
          restored = saved[chartIndex]
            ? JSON.parse(JSON.stringify(saved[chartIndex]))
            : null;
        } else if (saved && chartIndex === 0) {
          restored = JSON.parse(JSON.stringify(saved));
        }
        if (!restored && sourceAssessment) {
          restored = JSON.parse(JSON.stringify(
            this.teamChartConfigAt(sourceAssessment, chartIndex),
          ));
        }
        tab.chartConfigs[chartIndex] = restored || updatedChartConfig;
        tab.chartConfig = tab.chartConfigs[chartIndex];
        continue;
      }

      // Bar chart comparison: left dropdown = grey + left, right dropdown = config color + right
      const barChartConfigColor = (() => {
        const bg = colorSource?.backgroundColor;
        const bc = colorSource?.borderColor;
        const color = (typeof bg === 'string' ? bg : bg?.[0]) || (typeof bc === 'string' ? bc : bc?.[0]);
        const normalized = color && (color.startsWith('#') ? color : `#${color}`);
        return normalized || '#f97316';
      })();
      const barChartGreyColor = '#9ca3af';

      const getCompareDateLabel = (a) => {
        const iso = this.getPeriodLatestSubmissionDateForAssessment(a);
        return iso ? formatLocaleDate(iso, this.$i18n?.locale) : '';
      };
      const order = 1; // Show behind standard deviation

      // Case 2: Only left dropdown – [left selection (grey), latest (config)]
      if (hasComparison1 && !hasComparison2) {
        const leftAssessment = this.groupedTeamAssessments[tab.assessmentType]?.find(
          (a) => a.assessmentId === this.selectedComparison1[assessmentId],
        );
        if (leftAssessment) {
          datasets.push({
            data: this.teamChartConfigAt(leftAssessment, chartIndex).data.datasets[0].data,
            label: `${this.getAssessmentName(leftAssessment.assessmentName)} ${getCompareDateLabel(leftAssessment)}`,
            backgroundColor: barChartGreyColor,
            borderColor: barChartGreyColor,
            borderWidth: 1,
            order,
          });
        }
        datasets.push({
          ...latestDatasetSource,
          label: `${this.getAssessmentName(tab.assessmentName)} ${getCompareDateLabel(tab)}`,
          backgroundColor: barChartConfigColor,
          borderColor: barChartConfigColor,
          order,
        });
      }

      // Case 3: Only right dropdown – [latest (grey), right selection (config)]; original config preserved for reset
      if (!hasComparison1 && hasComparison2) {
        datasets.push({
          ...latestDatasetSource,
          label: `${this.getAssessmentName(tab.assessmentName)} ${getCompareDateLabel(tab)}`,
          backgroundColor: barChartGreyColor,
          borderColor: barChartGreyColor,
          order,
        });
        const rightAssessment = this.groupedTeamAssessments[tab.assessmentType]?.find(
          (a) => a.assessmentId === this.selectedComparison2[assessmentId],
        );
        if (rightAssessment) {
          datasets.push({
            data: this.teamChartConfigAt(rightAssessment, chartIndex).data.datasets[0].data,
            label: `${this.getAssessmentName(rightAssessment.assessmentName)} ${getCompareDateLabel(rightAssessment)}`,
            backgroundColor: barChartConfigColor,
            borderColor: barChartConfigColor,
            borderWidth: 1,
            order,
          });
        }
      }

      // Case 4: Both dropdowns – [left selection (grey), right selection (config)]
      if (hasComparison1 && hasComparison2) {
        const leftAssessment = this.groupedTeamAssessments[tab.assessmentType]?.find(
          (a) => a.assessmentId === this.selectedComparison1[assessmentId],
        );
        const rightAssessment = this.groupedTeamAssessments[tab.assessmentType]?.find(
          (a) => a.assessmentId === this.selectedComparison2[assessmentId],
        );
        if (leftAssessment) {
          datasets.push({
            data: this.teamChartConfigAt(leftAssessment, chartIndex).data.datasets[0].data,
            label: `${this.getAssessmentName(leftAssessment.assessmentName)} ${getCompareDateLabel(leftAssessment)}`,
            backgroundColor: barChartGreyColor,
            borderColor: barChartGreyColor,
            borderWidth: 1,
            order,
          });
        }
        if (rightAssessment) {
          datasets.push({
            data: this.teamChartConfigAt(rightAssessment, chartIndex).data.datasets[0].data,
            label: `${this.getAssessmentName(rightAssessment.assessmentName)} ${getCompareDateLabel(rightAssessment)}`,
            backgroundColor: barChartConfigColor,
            borderColor: barChartConfigColor,
            borderWidth: 1,
            order,
          });
        }
      }

      // Add standard deviation if there are any comparisons
      if (hasComparison1 || hasComparison2) {
        // Get only the assessments being compared (not the main assessment)
        const comparedAssessments = [];
        // Add comparison assessments only
        if (hasComparison1) {
          const comparison1 = this.groupedTeamAssessments[tab.assessmentType]?.find(
            (a) => a.assessmentId === this.selectedComparison1[assessmentId],
          );
          if (comparison1) {
            comparedAssessments.push(comparison1);
          }
        }
        if (hasComparison2) {
          const comparison2 = this.groupedTeamAssessments[tab.assessmentType]?.find(
            (a) => a.assessmentId === this.selectedComparison2[assessmentId],
          );
          if (comparison2) {
            comparedAssessments.push(comparison2);
          }
        }
        // Include the main assessment for any comparisons (single or double)
        if (hasComparison1 || hasComparison2) {
          const mainAssessment = this.groupedTeamAssessments[tab.assessmentType]?.[0];
          if (mainAssessment) {
            comparedAssessments.push(mainAssessment);
          }
        }
        // Get the correct dimension keys from the first assessment
        let dimensionKeys = [];
        if (comparedAssessments.length > 0) {
          const firstAssessment = comparedAssessments[0];
          if (this.usesStandardDimensionsStructure(firstAssessment)) {
            dimensionKeys = Object.keys(firstAssessment.results.dimensions);
          } else if (this.usesQuestionArrayStructure(firstAssessment)) {
            dimensionKeys = firstAssessment.assessmentQuestionArray
              .map((q) => q.properties?.dimensionName)
              .filter((name) => name);
          }
        }
        // Calculate standard deviation for each dimension using only compared assessments
        const errorBars = tab.chartConfig.data.labels.map((label) => {
          // Find the corresponding dimension key for this label
          const dimensionKey = dimensionKeys.find((key) => {
            // For standard dimensions, the key should match the label
            if (this.usesStandardDimensionsStructure(comparedAssessments[0])) {
              return key === label;
            }
            // For question array, check if the dimension name matches
            if (this.usesQuestionArrayStructure(comparedAssessments[0])) {
              return key === label;
            }
            return false;
          });
          if (dimensionKey) {
            const stats = this.calculateDimensionStats(comparedAssessments, dimensionKey);
            return stats.stdDev;
          }
          return 0; // Return 0 if no matching dimension found
        });

        // Add standard deviation as overlapping bars
        datasets.push({
          label: this.$t('TeamResultDashboardComponent.standardDeviation'),
          data: errorBars,
          type: 'bar',
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          borderColor: '#000000',
          borderWidth: 2,
          order: -1, // Show in front of main data
          xAxisID: 'bar-x-axis2', // Use hidden second x-axis for offset
          // Use default y axis (no yAxisID specified)
        });
      }

      // Add dimension average as a separate bar when enabled (including when comparisons are shown)
      const avgData = tab.chartConfig.averageData;
      if (avgData?.enabled && avgData?.dimensions?.length && updatedChartConfig.data?.labels) {
        // eslint-disable-next-line prefer-destructuring
        const labels = updatedChartConfig.data.labels;
        const mainData = updatedChartConfig.data.datasets[0]?.data;
        if (Array.isArray(mainData) && mainData.length === labels.length) {
          const valueByLabel = labels.reduce((acc, label, i) => {
            acc[label] = mainData[i];
            return acc;
          }, {});
          const avgDimensions = avgData.dimensions;
          const avgWeights = avgData.weights || {};
          let weightedSum = 0;
          let totalWeight = 0;
          avgDimensions.forEach((dim) => {
            const value = valueByLabel[dim];
            if (value !== undefined && value !== null) {
              const weight = avgWeights[dim] ?? 1;
              weightedSum += Number(value) * weight;
              totalWeight += weight;
            }
          });
          if (totalWeight > 0) {
            const roundedAverage = roundToTwoDecimals(weightedSum / totalWeight);
            const locale = this.$i18n?.locale || 'en';
            const averageLabelText = locale === 'de' ? (avgData.title?.de) : (avgData.title?.en) || 'Average';
            // Add "Average" as a new category so it appears as a separate bar
            updatedChartConfig.data.labels = [...labels, averageLabelText];
            // Extend all existing datasets with null so they don't show a bar in the Average column
            datasets.forEach((ds) => {
              if (Array.isArray(ds.data)) ds.data.push(null);
            });
            datasets.push({
              label: averageLabelText,
              data: [...Array(labels.length).fill(null), roundedAverage],
              borderColor: avgData.borderColor?.startsWith('#') ? avgData.borderColor : `#${avgData.borderColor || '999'}`,
              backgroundColor: avgData.backgroundColor?.startsWith('#') ? avgData.backgroundColor : `#${avgData.backgroundColor || '999'}`,
              borderWidth: 2,
              order: 1,
            });
          }
        }
      }

      // Update the chart config with new datasets
      updatedChartConfig.data.datasets = datasets;

      // Set y-axis scale with min 0 and max 5 for all bar charts
      if (tab.chartConfig.type === 'bar') {
        // Check if standard deviation is present (has bar-x-axis2)
        const hasStandardDeviation = datasets.some(
          (dataset) => dataset.xAxisID === 'bar-x-axis2',
        );

        if (hasStandardDeviation) {
          // Configure axes for overlapping standard deviation
          // Keep original x and y axes, add hidden second x-axis for offset
          updatedChartConfig.options = {
            ...updatedChartConfig.options,
            scales: {
              ...updatedChartConfig.options?.scales,
              // Keep original x axis (default, no ID)
              x: {
                stacked: !hasComparisons,
                ...updatedChartConfig.options?.scales?.x,
              },
              // Hidden second x-axis for standard deviation offset
              'bar-x-axis2': {
                display: false,
                stacked: true,
                id: 'bar-x-axis2',
                position: 'bottom',
                barThickness: 70,
                type: 'category',
                categoryPercentage: 0.8,
                barPercentage: 0.9,
                offset: true,
              },
              // Keep original y axis (default, no ID)
              y: {
                stacked: false,
                max: this.config.chartScale.max,
                min: this.config.chartScale.min,
                ticks: {
                  beginAtZero: true,
                },
                ...updatedChartConfig.options?.scales?.y,
              },
            },
          };
        }
        // Standard configuration when no standard deviation
        if (updatedChartConfig.options && updatedChartConfig.options.scales) {
          if (updatedChartConfig.options.scales.y) {
            updatedChartConfig.options.scales.y.max = this.config.chartScale.max;
            updatedChartConfig.options.scales.y.min = this.config.chartScale.min;
            updatedChartConfig.options.scales.y.stacked = false; // Y-axis should never be stacked
          } else {
            updatedChartConfig.options.scales.y = {
              max: this.config.chartScale.max,
              min: this.config.chartScale.min,
              stacked: false, // Y-axis should never be stacked
            };
          }

          // Configure X-axis stacking based on comparisons
          if (updatedChartConfig.options.scales.x) {
            updatedChartConfig.options.scales.x.stacked = !hasComparisons;
          } else {
            updatedChartConfig.options.scales.x = {
              stacked: !hasComparisons,
            };
          }
        } else {
          updatedChartConfig.options = {
            ...updatedChartConfig.options,
            scales: {
              ...updatedChartConfig.options?.scales,
              y: {
                max: this.config.chartScale.max,
                min: this.config.chartScale.min,
                stacked: false, // Y-axis should never be stacked
              },
              x: {
                stacked: !hasComparisons, // Only stack when no comparisons
              },
            },
          };
        }
      }

      tab.chartConfigs[chartIndex] = updatedChartConfig;
      tab.chartConfig = updatedChartConfig;
      }

      tab.chartConfig = tab.chartConfigs[0];
    },

    calculateDimensionStats(assessments, dimensionKey) {
      const values = assessments
        .map((assessment) => {
          // Handle standard dimensions structure (preferred)
          if (this.usesStandardDimensionsStructure(assessment)) {
            return assessment.results?.dimensions?.[dimensionKey];
          }
          // Handle question array structure (fallback; mirrors backend RangeSliderProcessor)
          if (this.usesQuestionArrayStructure(assessment)) {
            const key = this.mapDimensionName(dimensionKey, assessment);
            return rangeSliderMeanForDimension(assessment.assessmentQuestionArray, key);
          }
          // Handle direct results structure (fallback)
          if (assessment.results) {
            return assessment.results[dimensionKey];
          }
          return undefined;
        })
        .filter((val) => val !== undefined && val !== null);

      if (values.length === 0) {
        return {
          stdDev: 0,
          mean: 0,
        };
      }

      return {
        stdDev: this.calculateStandardDeviation(values),
        mean: values.reduce((sum, val) => sum + val, 0) / values.length,
      };
    },

    usesQuestionArrayStructure(assessment) {
      return assessment.assessmentQuestionArray &&
             assessment.assessmentQuestionArray.length > 0 &&
             assessment.assessmentQuestionArray.some((q) => q.properties?.dimensionName);
    },

    usesStandardDimensionsStructure(assessment) {
      return assessment.results &&
             assessment.results.dimensions &&
             Object.keys(assessment.results.dimensions).length > 0;
    },

    /**
     * Map team period rows to the shape expected by ResultsComparisonSection (_id + date field).
     */
    teamSameTypeAssessmentsForComparison(tab) {
      return (this.groupedTeamAssessments[tab.assessmentType] || []).map((a) => ({
        _id: a.assessmentId,
        assessmentName: a.assessmentName,
        date_assessmentMade: a.submittedAt || a.date_assessmentMade || a.date,
      }));
    },

    findTeamComparisonAssessment(comparisonId, assessmentType) {
      if (!comparisonId) return null;
      return this.groupedTeamAssessments[assessmentType]?.find(
        (a) => a.assessmentId === comparisonId,
      ) || null;
    },

    getComparisonScoresRecord(assessment) {
      const out = {};
      if (this.usesStandardDimensionsStructure(assessment)) {
        Object.entries(assessment.results.dimensions || {}).forEach(([k, v]) => {
          if (typeof v === 'number' && !Number.isNaN(v)) out[k] = v;
        });
        return out;
      }
      if (this.usesQuestionArrayStructure(assessment)) {
        return dimensionScoresFromRangeSliderQuestions(assessment.assessmentQuestionArray);
      }
      if (assessment.results && typeof assessment.results === 'object') {
        Object.entries(assessment.results).forEach(([k, v]) => {
          if (typeof v === 'number' && !Number.isNaN(v)) out[k] = v;
        });
      }
      return out;
    },

    getTeamComparisonDeltas(tab) {
      const comp1Id = this.selectedComparison1[tab.assessmentId];
      const comp2Id = this.selectedComparison2[tab.assessmentId];
      if (!comp1Id || !comp2Id) return null;

      const comp1 = this.findTeamComparisonAssessment(comp1Id, tab.assessmentType);
      const comp2 = this.findTeamComparisonAssessment(comp2Id, tab.assessmentType);
      if (!comp1 || !comp2) return null;

      const scores1 = this.getComparisonScoresRecord(comp1);
      const scores2 = this.getComparisonScoresRecord(comp2);

      return Object.keys(scores1)
        .filter((key) => scores2[key] !== undefined && typeof scores2[key] === 'number')
        .map((key) => ({
          key,
          label: this.getDimensionLabel(key),
          value: roundToTwoDecimals(scores2[key] - scores1[key]),
        }));
    },

    onComparison1Update(tab, v) {
      this.selectedComparison1[tab.assessmentId] = v;
      this.updateChartData(tab.assessmentId);
    },

    onComparison2Update(tab, v) {
      this.selectedComparison2[tab.assessmentId] = v;
      this.updateChartData(tab.assessmentId);
    },

    async resetComparison(assessmentId) {
      this.selectedComparison1[assessmentId] = '';
      this.selectedComparison2[assessmentId] = '';
      await this.updateChartData(assessmentId);
      // Force bar chart to re-mount with the restored latest-only config
      this.chartResetKeys = {
        ...this.chartResetKeys,
        [assessmentId]: (this.chartResetKeys[assessmentId] || 0) + 1,
      };
    },

    /**
     * Clear and reset chart configurations for all tabs
     * This ensures fresh chart configs are created on every load
     */
    clearAllChartConfigs() {
      if (this.latestTeamAssessments && this.latestTeamAssessments.length > 0) {
        this.latestTeamAssessments.forEach((tab) => {
          if (tab.chartConfigs?.length) {
            // eslint-disable-next-line no-param-reassign
            delete tab.chartConfigs;
          }
          if (tab.chartConfig) {
            // eslint-disable-next-line no-param-reassign
            delete tab.chartConfig;
          }
        });
      }
      // Clear original chart configs when changing teams
      this.originalChartConfigs = {};
      this.latestOnlyChartConfigs = {};
    },

    /**
     * Clear chart config for a specific assessment tab
     * @param {string} assessmentId - The assessment ID to clear
     */
    clearChartConfig(assessmentId) {
      const tab = this.latestTeamAssessments.find((t) => t.assessmentId === assessmentId);
      if (tab) {
        if (tab.chartConfigs) delete tab.chartConfigs;
        if (tab.chartConfig) delete tab.chartConfig;
      }
    },

    /**
     * Recreate chart config from original data
     * @param {string} assessmentId - The assessment ID to recreate
     */
    async recreateChartConfig(assessmentId) {
      const tab = this.latestTeamAssessments.find((t) => t.assessmentId === assessmentId);
      if (!tab) {
        return;
      }

      // Find the assessment from groupedTeamAssessments (it should have the chart config)
      const assessment = this.groupedTeamAssessments[tab.assessmentType]?.find(
        (a) => a.assessmentId === assessmentId,
      );

      if (assessment && assessment.chartConfigs?.length) {
        tab.chartConfigs = assessment.chartConfigs.map((c) => JSON.parse(JSON.stringify(c)));
        tab.chartConfig = tab.chartConfigs[0];
        return;
      }
      if (assessment && assessment.chartConfig) {
        tab.chartConfig = JSON.parse(JSON.stringify(assessment.chartConfig));
        tab.chartConfigs = [tab.chartConfig];
        return;
      }

      const originalData = this.originalChartConfigs[tab.assessmentType];
      if (!originalData) {
        console.warn(`No original chart config found for assessment type: ${tab.assessmentType}`);
        return;
      }

      const latestAssessment = this.groupedTeamAssessments[tab.assessmentType]?.[0];
      if (latestAssessment && latestAssessment.results) {
        const templates = originalData.chartConfigs?.length
          ? originalData.chartConfigs
          : [originalData.chartConfig];
        tab.chartConfigs = templates.map((tmpl) => this.createAverageChartConfig(
          tmpl,
          latestAssessment.results.dimensions,
          latestAssessment.standardDeviation?.dimensions || {},
          tab.assessmentType,
          originalData.assessmentName,
        ));
        tab.chartConfig = tab.chartConfigs[0];
      }
    },

    /**
     * Get original chart config for a specific assessment type
     * @param {string} assessmentType - The assessment type
     * @returns {Object|null} The original chart config data
     */
    getOriginalChartConfig(assessmentType) {
      return this.originalChartConfigs[assessmentType] || null;
    },

    // Smart time granularity selection based on data range
    selectSmartTimeGranularity(assessments) {
      if (assessments.length === 0) return 'weekly';

      const dates = assessments
        .map((a) => new Date(a.date_assessmentMade || a.submittedAt || a.date || a.createdAt))
        .sort((a, b) => a - b);

      const firstDate = dates[0];
      const lastDate = dates[dates.length - 1];
      const daysDiff = Math.ceil((lastDate - firstDate) / (1000 * 60 * 60 * 24));

      // Smart defaults based on data range
      if (daysDiff < 7) {
        return 'daily'; // Less than a week: show daily
      }
      if (daysDiff < 30) {
        return 'weekly'; // Less than a month: show weekly
      }
      return 'monthly'; // More than a month: show monthly
    },

    // Group assessments by time period based on selected granularity
    groupAssessmentsByTimePeriod(assessments, granularity) {
      if (granularity === 'daily') {
        // For daily, return individual assessments grouped by date
        const grouped = {};
        assessments.forEach((assessment) => {
          const date = new Date(assessment.date_assessmentMade
           || assessment.submittedAt || assessment.date || assessment.createdAt);
          const dateKey = date.toISOString().split('T')[0]; // YYYY-MM-DD
          if (!grouped[dateKey]) {
            grouped[dateKey] = [];
          }
          grouped[dateKey].push(assessment);
        });
        return grouped;
      }
      if (granularity === 'weekly') {
        // Group by week (Monday to Sunday)
        const grouped = {};
        assessments.forEach((assessment) => {
          const date = new Date(assessment.date_assessmentMade
           || assessment.submittedAt || assessment.date || assessment.createdAt);
          const weekStart = new Date(date);
          // Get Monday of the week (handle Sunday as day 0)
          const dayOfWeek = date.getDay();
          const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
          weekStart.setDate(date.getDate() + daysToMonday);
          weekStart.setHours(0, 0, 0, 0); // Set to start of day for consistency
          const weekKey = weekStart.toISOString().split('T')[0]; // YYYY-MM-DD format
          if (!grouped[weekKey]) {
            grouped[weekKey] = [];
          }
          grouped[weekKey].push(assessment);
        });
        return grouped;
      }
      if (granularity === 'monthly') {
        // Group by month
        const grouped = {};
        assessments.forEach((assessment) => {
          const date = new Date(assessment.date_assessmentMade
           || assessment.submittedAt || assessment.date || assessment.createdAt);
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`; // YYYY-MM-01 format
          if (!grouped[monthKey]) {
            grouped[monthKey] = [];
          }
          grouped[monthKey].push(assessment);
        });
        return grouped;
      }
      return {};
    },

    // Map chart dimension names to assessment data dimension names
    mapDimensionName(chartDimensionName, assessment) {
      // If assessment has assessmentQuestionArray, try to find matching dimension
      if (assessment?.assessmentQuestionArray?.length > 0) {
        // First try exact match
        let question = assessment.assessmentQuestionArray
          .find((q) => q.properties?.dimensionName === chartDimensionName);

        if (question) return chartDimensionName;

        // Try to find by label matching (dynamic approach)
        question = assessment.assessmentQuestionArray
          .find((q) => {
            const dimensionName = q.properties?.dimensionName;
            const dimensionLabel = this.getDimensionLabel(dimensionName);
            return dimensionLabel === chartDimensionName;
          });

        if (question) return question.properties?.dimensionName;
      }

      return chartDimensionName; // Return original if no mapping found
    },

    // Calculate aggregated data for a group of assessments
    calculateAggregatedData(groupedAssessments, dimensionKey) {
      const aggregatedData = [];

      Object.keys(groupedAssessments).sort().forEach((timeKey) => {
        const assessments = groupedAssessments[timeKey];
        if (assessments.length === 0) return;

        // Calculate average value for this time period
        const values = assessments
          .map((assessment) => {
            if (this.usesStandardDimensionsStructure(assessment)) {
              const v = assessment.results?.dimensions?.[dimensionKey];
              if (v !== undefined && v !== null) return v;
            }
            if (this.usesQuestionArrayStructure(assessment)) {
              const actualDimensionName = this.mapDimensionName(dimensionKey, assessment);
              return rangeSliderMeanForDimension(
                assessment.assessmentQuestionArray,
                actualDimensionName,
              );
            }
            if (assessment.results?.dimensions?.[dimensionKey] !== undefined) {
              return assessment.results.dimensions[dimensionKey];
            }
            if (assessment.results?.[dimensionKey] !== undefined) {
              return assessment.results[dimensionKey];
            }
            return null;
          })
          .filter((value) => value !== null && value !== undefined);

        if (values.length > 0) {
          const average = values.reduce((sum, val) => sum + val, 0) / values.length;
          const roundedAverage = roundToTwoDecimals(average);

          aggregatedData.push({
            timeKey, // Use original timeKey (ISO timestamp) for matching
            value: roundedAverage,
            count: values.length,
          });
        }
      });

      return aggregatedData;
    },

    async changeTab(tabId) {
      this.selectedTab = tabId;
      this.selectedTimePeriod = 'all'; // Reset time period when changing tabs

      // Clear chart config for the new tab to ensure fresh creation
      // this.clearChartConfig(tabId);

      // Only reset time granularity to smart default for line charts on initial load
      // Don't override user's manual selection when switching between tabs
      const tab = this.latestTeamAssessments.find((t) => t.assessmentId === tabId);

      if (tab && tab.chartConfigs?.some((c) => c.type === 'bar') &&
        this.groupedTeamAssessments[tab.assessmentType]?.length >= 1) {
        // Clear comparison selections for this tab
        this.resetComparison(tabId);
      }

      if (tab && this.teamLineChartConfig(tab) && !this.selectedTimeGranularity) {
        // Get assessments for smart granularity selection
        let allAssessments = [];
        if (this.rawTeamAssessments && this.rawTeamAssessments.length > 0) {
          allAssessments = this.rawTeamAssessments
            .filter((assessment) => assessment.type === tab.assessmentType);
        }
        this.selectedTimeGranularity = this.selectSmartTimeGranularity(allAssessments);
      }

      await this.updateChartData(tabId);
    },

    async onTimePeriodChange(timePeriod, customDateRange = null) {
      // Update chart data when time period changes
      this.selectedTimePeriod = timePeriod;
      this.customDateRange = customDateRange; // Store custom date range
      if (this.selectedTab) {
        await this.updateChartData(this.selectedTab);
      }
    },

    async onTimeGranularityChange() {
      // Handle time granularity change for line charts
      if (this.selectedTab) {
        // Only update if it's a line chart
        const tab = this.latestTeamAssessments.find((t) => t.assessmentId === this.selectedTab);
        if (tab && this.teamLineChartConfig(tab)) {
          this.$forceUpdate();
          await this.updateChartData(this.selectedTab);
        }
      }
    },

    onCustomDateChange() {
      // Auto-apply when both dates are selected
      if (this.customStartDate && this.customEndDate) {
        this.applyCustomRange();
      }
    },

    applyCustomRange() {
      if (this.customStartDate && this.customEndDate) {
        // Validate that start date is before end date
        if (new Date(this.customStartDate) > new Date(this.customEndDate)) {
          alert(this.$t('TimePeriodSelector.invalidDateRange') || 'Start date must be before end date');
          return;
        }

        // Emit custom date range with both dates
        this.onTimePeriodChange('custom', {
          startDate: this.customStartDate,
          endDate: this.customEndDate,
        });
      }
    },

    /**
     * Filter assessments by selected time period
     * @param {Array} assessments - Array of assessments to filter
     * @returns {Array} Filtered assessments
     */
    filterAssessmentsByTimePeriod(assessments) {
      if (this.selectedTimePeriod === 'all') {
        return assessments;
      }

      // Handle custom date range
      if (this.selectedTimePeriod === 'custom' && this.customDateRange) {
        const startDate = new Date(this.customDateRange.startDate);
        const endDate = new Date(this.customDateRange.endDate);

        return assessments.filter((assessment) => {
          const assessmentDate = new Date(assessment.date_assessmentMade ||
            assessment.submittedAt ||
            assessment.date ||
            assessment.createdAt ||
            new Date());
          return assessmentDate >= startDate && assessmentDate <= endDate;
        });
      }

      const now = new Date();
      let cutoffDate;

      switch (this.selectedTimePeriod) {
        case 'week':
          cutoffDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'month':
          cutoffDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case 'quarter':
          cutoffDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          break;
        case 'year':
          cutoffDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
          break;
        default:
          return assessments;
      }

      return assessments.filter((assessment) => {
        const assessmentDate = new Date(assessment.date_assessmentMade ||
          assessment.submittedAt ||
          assessment.date ||
          assessment.createdAt ||
          new Date());
        return assessmentDate >= cutoffDate;
      });
    },

    createAverageDataset(assessments, chartConfig, labels, groupedAssessments = null) {
      const { averageData } = chartConfig;
      if (!averageData || !averageData.enabled || !averageData.dimensions?.length) {
        return null;
      }

      const {
        dimensions: avgDimensions,
        weights: avgWeights,
        title,
        backgroundColor,
        borderColor,
      } = averageData;
      const locale = this.$i18n?.locale || 'en';
      const label = locale === 'de' ? title.de : title.en;

      // If we have grouped assessments (for time granularity), use them directly
      if (groupedAssessments) {
        const data = labels.map((timeKey) => {
          const timeAssessments = groupedAssessments[timeKey] || [];
          if (timeAssessments.length === 0) return null;

          let weightedSum = 0;
          let totalWeight = 0;

          avgDimensions.forEach((dim) => {
            const values = timeAssessments
              .map((a) => {
                if (this.usesStandardDimensionsStructure(a)) {
                  const v = a.results?.dimensions?.[dim];
                  if (v !== undefined && v !== null) return v;
                }
                if (this.usesQuestionArrayStructure(a)) {
                  const actualDimensionName = this.mapDimensionName(dim, a);
                  return rangeSliderMeanForDimension(
                    a.assessmentQuestionArray,
                    actualDimensionName,
                  );
                }
                if (a.results?.dimensions?.[dim] !== undefined) {
                  return a.results.dimensions[dim];
                }
                if (a.results?.[dim] !== undefined) {
                  return a.results[dim];
                }
                return null;
              })
              .filter((value) => value !== undefined && value !== null);

            if (values.length > 0) {
              const average = values.reduce((sum, val) => sum + val, 0) / values.length;
              const weight = avgWeights[dim] || 1;
              weightedSum += average * weight;
              totalWeight += weight;
            }
          });

          if (totalWeight === 0) return null;

          const average = weightedSum / totalWeight;
          const roundedAverage = roundToTwoDecimals(average);

          // Return {x, y} object for time series
          return {
            x: timeKey, // ISO timestamp
            y: roundedAverage,
          };
        }).filter((item) => item !== null); // Remove null values

        return {
          label,
          data,
          borderColor: borderColor || '#rgb(255, 107, 107)',
          backgroundColor: backgroundColor || '#rgba(255, 107, 107, 0.5)',
          fill: false,
          tension: 0.1,
        };
      }

      // Fallback to original logic for individual assessments
      const assessmentsByWeek = {};
      assessments.forEach((assessment) => {
        const assessmentDate = new Date(assessment.date_assessmentMade
          || assessment.submittedAt
          || assessment.date || assessment.createdAt || new Date());
        const weekStart = new Date(assessmentDate);
        weekStart.setDate(assessmentDate.getDate() - assessmentDate.getDay() + 1);
        weekStart.setHours(12, 0, 0, 0);

        const weekKey = weekStart.toISOString();
        if (!assessmentsByWeek[weekKey]) {
          assessmentsByWeek[weekKey] = [];
        }
        assessmentsByWeek[weekKey].push(assessment);
      });

      // Calculate average for each week using {x, y} objects
      const data = labels.map((weekKey) => {
        const weekAssessments = assessmentsByWeek[weekKey] || [];
        if (weekAssessments.length === 0) return null;

        let weightedSum = 0;
        let totalWeight = 0;

        avgDimensions.forEach((dim) => {
          const values = weekAssessments
            .map((a) => {
              let value = null;

              if (this.usesStandardDimensionsStructure(a)) {
                const v = a.results?.dimensions?.[dim];
                if (v !== undefined && v !== null) value = v;
              }
              if (value == null && this.usesQuestionArrayStructure(a)) {
                const actualDimensionName = this.mapDimensionName(dim, a);
                value = rangeSliderMeanForDimension(
                  a.assessmentQuestionArray,
                  actualDimensionName,
                );
              }
              if (value == null && a.results?.dimensions?.[dim] !== undefined) {
                value = a.results.dimensions[dim];
              } else if (value == null && a.results?.[dim] !== undefined) {
                value = a.results[dim];
              }

              return value;
            })
            .filter((value) => value !== undefined && value !== null);

          if (values.length > 0) {
            const average = values.reduce((sum, val) => sum + val, 0) / values.length;
            const weight = avgWeights[dim] || 1;
            weightedSum += average * weight;
            totalWeight += weight;
          }
        });

        if (totalWeight === 0) return null;

        const average = weightedSum / totalWeight;
        const roundedAverage = roundToTwoDecimals(average);

        // Return {x, y} object for time series
        return {
          x: weekKey, // ISO timestamp
          y: roundedAverage,
        };
      }).filter((item) => item !== null); // Remove null values

      const averageDataset = {
        label,
        data,
        borderColor: borderColor?.startsWith('#') ? borderColor : `#${borderColor}`,
        backgroundColor: backgroundColor?.startsWith('#') ? backgroundColor : `#${backgroundColor}`,
        fill: false,
        tension: 0.4,
        borderWidth: 2,
        pointRadius: 2,
        spanGaps: true, // This will connect points across gaps
      };

      return averageDataset;
    },

    async updateChartData(assessmentId) {
      let tab = this.latestTeamAssessments.find((t) => t.assessmentId === assessmentId);

      if (!tab) {
        return;
      }

      // If chart config is missing, recreate it from original data
      if (!tab.chartConfigs?.length && !tab.chartConfig) {
        await this.recreateChartConfig(assessmentId);
        tab = this.latestTeamAssessments.find((t) => t.assessmentId === assessmentId);
        if (!tab || (!tab.chartConfig && !tab.chartConfigs?.length)) {
          return;
        }
      }
      if (!tab.chartConfigs?.length && tab.chartConfig) {
        tab.chartConfigs = [tab.chartConfig];
      }

      const hasAnyLineChart = tab.chartConfigs.some((c) => c.type === 'line');

      // Handle every line chart on the tab (multi-chart splits dimensions across charts).
      if (hasAnyLineChart) {
        let allAssessments = [];

        if (this.rawTeamAssessments && this.rawTeamAssessments.length > 0) {
          allAssessments = this.rawTeamAssessments
            .filter((assessment) => assessment.type === tab.assessmentType)
            .slice()
            .sort((a, b) => new Date(a.date_assessmentMade
             || a.submittedAt
             || a.date
             || a.createdAt
             || new Date()) - new Date(b.date_assessmentMade
              || b.submittedAt || b.date || b.createdAt || new Date()));
        }

        if (allAssessments.length === 0) {
          allAssessments = (this.$store.state.assessments.teamAssessments || [])
            .filter((assessment) => assessment.assessmentType === tab.assessmentType)
            .slice()
            .sort((a, b) => new Date(a.date_assessmentMade
             || a.submittedAt
             || a.date
             || a.createdAt || new Date()) - new Date(b.date_assessmentMade
              || b.submittedAt || b.date || b.createdAt || new Date()));
        }

        allAssessments = this.filterAssessmentsByTimePeriod(allAssessments);

        const groupedAssessments = this
          .groupAssessmentsByTimePeriod(allAssessments, this.selectedTimeGranularity);

        const labels = Object.keys(groupedAssessments).sort();

        let timeUnit = 'week';
        if (this.selectedTimeGranularity === 'daily') {
          timeUnit = 'day';
        } else if (this.selectedTimeGranularity === 'monthly') {
          timeUnit = 'month';
        }

        const timeScaleConfig = {
          type: 'time',
          time: {
            unit: timeUnit,
            isoWeekday: true,
            displayFormats: {
              day: 'MMM d, yyyy',
              week: 'MMM d, yyyy',
              month: 'MMM yyyy',
            },
          },
          reverse: false,
        };

        const yAxisConfig = {
          min: this.config.chartScale.min,
          max: this.config.chartScale.max,
        };

        for (let lineIdx = 0; lineIdx < tab.chartConfigs.length; lineIdx += 1) {
          if (tab.chartConfigs[lineIdx].type !== 'line') {
            continue;
          }

          tab.chartConfig = tab.chartConfigs[lineIdx];

          let dimensionKeys = tab.chartConfig.dimensions || [];

          if (!dimensionKeys || dimensionKeys.length === 0) {
            const firstAssessment = allAssessments[0];
            if (firstAssessment && this.usesStandardDimensionsStructure(firstAssessment)) {
              dimensionKeys = Object.keys(firstAssessment.results.dimensions);
            } else if (firstAssessment && this.usesQuestionArrayStructure(firstAssessment)) {
              dimensionKeys = firstAssessment.assessmentQuestionArray
                .map((q) => q.properties?.dimensionName)
                .filter((name) => name);
            }
          }

          const datasets = dimensionKeys.map((dimensionKey, idx) => {
            const dimensionLabel = this.getDimensionLabel(dimensionKey);
            const aggregatedData = this.calculateAggregatedData(groupedAssessments, dimensionKey);

            const data = labels.map((timeKey) => {
              const aggregatedItem = aggregatedData.find((item) => item.timeKey === timeKey);
              return aggregatedItem ? {
                x: timeKey,
                y: aggregatedItem.value,
              } : null;
            }).filter((item) => item !== null);

            return {
              label: dimensionLabel,
              data,
              borderColor:
                tab.chartConfig.data.datasets[idx]?.borderColor
                || `hsl(${idx * 60}, 70%, 50%)`,
              backgroundColor:
                tab.chartConfig.data.datasets[idx]?.backgroundColor
                || `hsl(${idx * 60}, 70%, 80%)`,
              fill: false,
              tension: 0.4,
              spanGaps: true,
            };
          });

          if (tab.chartConfig.averageData && tab.chartConfig.averageData.enabled) {
            const averageDataset = this.createAverageDataset(
              allAssessments,
              tab.chartConfig,
              labels,
              groupedAssessments,
            );
            if (averageDataset) {
              datasets.push(averageDataset);
            }
          }

          const updatedChartConfig = JSON.parse(JSON.stringify(tab.chartConfig));
          updatedChartConfig.data.labels = [];
          updatedChartConfig.data.datasets = datasets;

          if (updatedChartConfig.options && updatedChartConfig.options.scales) {
            updatedChartConfig.options.scales.x = timeScaleConfig;
            updatedChartConfig.options.scales.y = {
              ...updatedChartConfig.options.scales.y,
              ...yAxisConfig,
            };
          } else {
            updatedChartConfig.options = {
              ...updatedChartConfig.options,
              scales: {
                ...updatedChartConfig.options?.scales,
                x: timeScaleConfig,
                y: yAxisConfig,
              },
            };
          }

          tab.chartConfigs[lineIdx] = updatedChartConfig;
        }
      }

      // Process each chart by index: native boxplot, bar (with optional bar→boxplot), barWithErrorBars.
      // Do not return early when a line chart exists — other charts on the same tab must still update.
      let needsBarComparisonUpdate = false;
      for (let chartIndex = 0; chartIndex < tab.chartConfigs.length; chartIndex += 1) {
        const cfg = tab.chartConfigs[chartIndex];
        tab.chartConfig = cfg;

        if (cfg.type === 'line') {
          continue;
        }

        // Native boxplot from CMS: build quartile data from team memberDimensionValues (not plain numeric series).
        if (cfg.type === 'boxplot') {
          const boxplotConfig = this.buildTeamAggregatedBoxplotConfig(tab, chartIndex);
          if (boxplotConfig) {
            tab.chartConfigs[chartIndex] = boxplotConfig;
          }
          continue;
        }

        if (cfg.type === 'bar' || cfg.type === 'barWithErrorBars') {
          if (
            this.isBarComparisonMode(tab, chartIndex)
            && this.shouldRenderTeamBarChartAsBoxplot(tab, chartIndex)
          ) {
            const boxplotConfig = this.buildTeamAggregatedBoxplotConfig(tab, chartIndex);
            if (boxplotConfig) {
              tab.chartConfigs[chartIndex] = boxplotConfig;
            } else {
              needsBarComparisonUpdate = true;
            }
          } else {
            needsBarComparisonUpdate = true;
          }
        }
      }
      tab.chartConfig = tab.chartConfigs[0];
      if (needsBarComparisonUpdate) {
        this.updateBarChartWithComparisons(assessmentId);
      }
    },

    async onTeamChange() {
      try {
        this.loading = true;

        let teamMembers = [];

        if (this.selectedTeamId === 'all') {
          // Get all team members from all teams (excluding configured teams)
          const filteredTeams = this.teamsForClientDashboard;
          teamMembers = filteredTeams
            .reduce((members, team) => [...members, ...team.members], []);
        } else {
          // Get members from selected team only
          const selectedTeam = this.leadedTeams.find((team) => team._id === this.selectedTeamId);
          teamMembers = selectedTeam ? selectedTeam.members : [];
        }

        // Remove duplicates
        const uniqueTeamMembers = [...new Set(teamMembers)];

        // Fetch team assessment results for selected team
        const rawAssessments = await this.getTeamAssessmentsResults({
          teamMembers: uniqueTeamMembers,
          status: 'submitted',
        });

        // Store raw assessments for line charts
        this.rawTeamAssessments = rawAssessments || [];
        // Process the data
        const processedAssessments = await this.processTeamAssessmentData();
        this.$store.commit('assessments/SET_TEAM_ASSESSMENTS', processedAssessments);

        // Set initial selected tab if we have assessments
        if (processedAssessments.length > 0) {
          this.selectedTab = processedAssessments[0].assessmentId;
          // Update chart data for the selected tab (especially needed for line charts)
          await this.updateChartData(this.selectedTab);
        } else {
          this.selectedTab = null;
        }

        this.loading = false;
      } catch (error) {
        this.error = 'Failed to load team assessment results';
        this.loading = false;
      }
    },

    /**
     * Get translated assessment name for display purposes
     * @param {Object} assessmentName - The assessment name object with en/de properties
     * @returns {string} The translated assessment name
     */
    getAssessmentName(assessmentName) {
      const locale = this.$i18n?.locale || 'en';
      return assessmentName?.[locale] || assessmentName?.en || '';
    },

    /**
     * Get translated dimension label for display purposes
     * @param {string} dimensionValue - The dimension value
     * @returns {string} The translated label
     */
    getDimensionLabel(dimensionValue) {
      const dimension = this.dimensions.find((d) => d.value === dimensionValue);
      const locale = this.$i18n?.locale || 'en';
      return dimension?.translation?.[locale] || dimensionValue;
    },

    /**
     * Determine if an assessment should have fixed y-axis scale (0-6)
     * @param {Object} assessment - The assessment object or tab object
     * @returns {boolean} True if should have fixed scale
     */
    shouldHaveFixedYAxisScale(assessment) {
      // For processed tab objects, check if they have question array structure
      if (assessment.assessmentType) {
        // This is a processed tab object, check the original assessment type
        const originalAssessment = this.groupedAssessments?.[assessment.assessmentType]?.[0];
        if (originalAssessment) {
          return !this.usesQuestionArrayStructure(originalAssessment);
        }
        // Fallback: assume standard structure if we can't find original
        return true;
      }
      // For raw assessment objects, use question array structure as indicator
      return !this.usesQuestionArrayStructure(assessment);
    },

    async processTeamAssessmentData() {
      const { teamAssessments } = this;
      const { assessmentSchemas: schemas } = this;
      const { charts } = this;

      // Check if teamAssessments is valid
      if (!teamAssessments || !Array.isArray(teamAssessments) || teamAssessments.length === 0) {
        return [];
      }

      // Step 1: Group assessments by type, then create monthly periods
      // Each period starts with the first assessment date and spans one month
      const assessmentsByTypeAndPeriod = {};

      // First, group by type
      const assessmentsByType = {};
      teamAssessments.forEach((assessment) => {
        // eslint-disable-next-line prefer-destructuring
        const type = assessment.type;
        if (!assessmentsByType[type]) {
          assessmentsByType[type] = [];
        }
        assessmentsByType[type].push(assessment);
      });

      // For each type, create monthly periods
      Object.entries(assessmentsByType).forEach(([type, assessments]) => {
        // Sort assessments by date (using date_taskBegin or fallback)
        const sortedAssessments = assessments.sort((a, b) => {
          const dateA = new Date(a.date_taskBegin
            || a.date_assessmentMade
            || a.submittedAt
            || a.date
            || a.createdAt
            || new Date());
          const dateB = new Date(b.date_taskBegin
            || b.date_assessmentMade
            || b.submittedAt
            || b.date
            || b.createdAt
            || new Date());
          return dateA - dateB;
        });

        // Group into monthly periods
        const periods = [];
        let currentPeriod = null;

        sortedAssessments.forEach((assessment) => {
          const assessmentDate = new Date(assessment.date_taskBegin
            || assessment.date_assessmentMade
            || assessment.submittedAt
            || assessment.date
            || assessment.createdAt
            || new Date());

          // If no current period or assessment is outside the current period's month range
          if (!currentPeriod || assessmentDate >= currentPeriod.endDate) {
            // Start a new period
            const periodStart = new Date(assessmentDate);
            periodStart.setHours(0, 0, 0, 0);
            const periodEnd = new Date(periodStart);
            periodEnd.setMonth(periodEnd.getMonth() + 1); // One month from start

            currentPeriod = {
              startDate: periodStart,
              endDate: periodEnd,
              periodKey: periodStart.toISOString().split('T')[0], // Use start date as key
              assessments: [],
            };
            periods.push(currentPeriod);
          }

          // Add assessment to current period
          currentPeriod.assessments.push(assessment);
        });

        // Store periods by type
        if (!assessmentsByTypeAndPeriod[type]) {
          assessmentsByTypeAndPeriod[type] = {};
        }
        periods.forEach((period) => {
          assessmentsByTypeAndPeriod[type][period.periodKey] = period.assessments;
        });
      });

      // Step 2: Calculate averages for each assessment type and period
      const processedAssessments = [];

      Object.entries(assessmentsByTypeAndPeriod).forEach(([type, periodGroups]) => {
        // Find matching schema
        const schema = schemas.find((s) => s.type === type);
        if (!schema) {
          return;
        }

        const schemaCharts = charts.filter(
          (c) => c.assessmentId === schema._id
            && ['team', 'both'].includes(c.visibility || 'both'),
        );

        if (!schemaCharts.length) {
          return;
        }

        // Process each assessment period separately
        Object.entries(periodGroups).forEach(([periodKey, assessments]) => {
          // Get all valid assessments for this type and period
          let validAssessments;

          // Check if this assessment type uses question array structure
          const firstAssessment = assessments[0];
          if (!firstAssessment) return;

          // Prioritize results.dimensions over question array structure for team results
          if (firstAssessment.results && firstAssessment.results.dimensions
           && Object.keys(firstAssessment.results.dimensions).length > 0) {
            // Use standard dimensions structure (preferred for team results)
            validAssessments = assessments
              .filter((assessment) => assessment.results && assessment.results.dimensions);
          } else if (this.usesQuestionArrayStructure(firstAssessment)) {
            // Fallback to question array structure if no results.dimensions
            validAssessments = assessments
              .filter((assessment) => assessment._id &&
              this.usesQuestionArrayStructure(assessment));
          } else {
            // Standard validation for other assessment types
            validAssessments = assessments
              .filter((assessment) => assessment.results && assessment.results.dimensions);
          }

          // Only show results if minimum participants have completed this assessment
          if (validAssessments.length < this.minParticipants) {
            return;
          }

          // Calculate average results and standard deviation for each dimension
          const averageResults = {};
          const standardDeviationResults = {};
          /** Raw member scores per dimension key (for team boxplots — quartiles across members). */
          const memberDimensionValues = {};

          // Prioritize results.dimensions over question array structure for bar charts
          if (firstAssessment.results && firstAssessment.results.dimensions
           && Object.keys(firstAssessment.results.dimensions).length > 0) {
            // Standard processing for assessments with results.dimensions (PREFERRED)
            const dimensionKeys = Object.keys(validAssessments[0].results.dimensions);

            dimensionKeys.forEach((dimensionKey) => {
              const values = validAssessments
                .map((assessment) => assessment.results.dimensions[dimensionKey])
                .filter((value) => value !== undefined && value !== null);

              if (values.length > 0) {
                memberDimensionValues[dimensionKey] = values.map((v) => Number(v));
                const average = values.reduce((sum, val) => sum + val, 0) / values.length;
                const standardDeviation = this.calculateStandardDeviation(values);

                averageResults[dimensionKey] = Math
                  .round(average * 100) / 100; // Round to 2 decimal places
                standardDeviationResults[dimensionKey] = Math
                  .round(standardDeviation * 100) / 100; // Round to 2 decimal places
              }
            });
          } else if (this.usesQuestionArrayStructure(firstAssessment)) {
            // Fallback to question array structure if no results.dimensions
            // Map question array data to average results
            const dimensionKeys = firstAssessment.assessmentQuestionArray
              .map((q) => q.properties?.dimensionName)
              .filter((name) => name);

            dimensionKeys.forEach((dimensionKey) => {
              const values = validAssessments
                .map((assessment) => {
                  const question = assessment.assessmentQuestionArray
                    .find((q) => q.properties?.dimensionName === dimensionKey);
                  return question?.properties?.data?.value;
                })
                .filter((value) => value !== undefined && value !== null);

              if (values.length > 0) {
                memberDimensionValues[dimensionKey] = values.map((v) => Number(v));
                const average = values.reduce((sum, val) => sum + val, 0) / values.length;
                const standardDeviation = this.calculateStandardDeviation(values);

                averageResults[dimensionKey] = Math
                  .round(average * 100) / 100; // Round to 2 decimal places
                standardDeviationResults[dimensionKey] = Math
                  .round(standardDeviation * 100) / 100; // Round to 2 decimal places
              }
            });
          } else {
            // Standard processing for other assessment types
            const dimensionKeys = Object.keys(validAssessments[0].results.dimensions);

            dimensionKeys.forEach((dimensionKey) => {
              const values = validAssessments
                .map((assessment) => assessment.results.dimensions[dimensionKey])
                .filter((value) => value !== undefined && value !== null);

              if (values.length > 0) {
                memberDimensionValues[dimensionKey] = values.map((v) => Number(v));
                const average = values.reduce((sum, val) => sum + val, 0) / values.length;
                const standardDeviation = this.calculateStandardDeviation(values);

                averageResults[dimensionKey] = Math
                  .round(average * 100) / 100; // Round to 2 decimal places
                standardDeviationResults[dimensionKey] = Math
                  .round(standardDeviation * 100) / 100; // Round to 2 decimal places
              }
            });
          }

          // Store original chart templates for recreation (only once per type, not per period)
          if (!this.originalChartConfigs[type]) {
            this.originalChartConfigs[type] = {
              chartConfigs: schemaCharts.map((c) => JSON.parse(JSON.stringify(c))),
              chartConfig: JSON.parse(JSON.stringify(schemaCharts[0])),
              type,
              assessmentName: schema.representationProperties?.assessmentName,
            };
          }

          const chartConfigs = schemaCharts.map((scm) => this.createAverageChartConfig(
            scm,
            averageResults,
            standardDeviationResults,
            type,
            schema.representationProperties?.assessmentName,
          ));

          // Create processed assessment with average results and standard deviation
          // Use the period date (date_taskBegin) for the assessment period
          const processedAssessment = {
            assessmentId: `team-average-${type}-${periodKey}`,
            periodKey,
            assessmentType: type,
            assessmentName: schema.representationProperties?.assessmentName,
            submittedAt: new Date(periodKey).toISOString(), // Period start (display uses latest submission in period)
            results: { dimensions: averageResults },
            standardDeviation: { dimensions: standardDeviationResults },
            memberDimensionValues,
            dimensions: schema.dimensions,
            chartConfigs,
            chartConfig: chartConfigs[0],
            teamMembers: validAssessments.map((assessment) => ({
              _id: assessment.assigned_to || assessment._id,
              firstName: assessment.assigned_to || assessment._id,
              lastName: '',
              email: '',
            })),
            isTeamAverage: true,
            participantCount: validAssessments.length,
          };

          processedAssessments.push(processedAssessment);
          // Keep immutable copy for reset (tab and sourceAssessment are same ref, so tab.chartConfig gets mutated)
          this.latestOnlyChartConfigs[processedAssessment.assessmentId] = chartConfigs.map(
            (c) => JSON.parse(JSON.stringify(c)),
          );
        });
      });

      // Group assessments by type for comparisons
      this.groupedTeamAssessments = processedAssessments
        .sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt))
        .reduce((acc, assessment) => {
          const { assessmentType } = assessment;
          if (!acc[assessmentType]) {
            acc[assessmentType] = [];
          }
          if (!acc[assessmentType].some((a) => a.assessmentId === assessment.assessmentId)) {
            acc[assessmentType].push(assessment);
          }
          return acc;
        }, {});

      return processedAssessments;
    },

    createAverageChartConfig(
      originalChartConfig,
      averageResults,
      standardDeviationResults,
      type,
      assessmentName = null,
    ) {
      if (!originalChartConfig || !originalChartConfig.data) {
        return originalChartConfig;
      }

      // Create a copy of the original chart config
      const chartConfig = JSON.parse(JSON.stringify(originalChartConfig));

      // Update the data with average results while preserving ALL original datasets
      if (chartConfig.data && chartConfig.data.labels && chartConfig.data.datasets
       && chartConfig.data.datasets.length > 0) {
        // eslint-disable-next-line prefer-destructuring
        const labels = chartConfig.data.labels;

        // Check if chartConfig.dimensions is available first
        // If available, use it to map values; otherwise fall back to averageResults keys
        const dimensionKeys = (chartConfig.dimensions && chartConfig.dimensions.length > 0)
          ? chartConfig.dimensions
          : Object.keys(averageResults);

        // Preserve ALL original datasets with their original colors and styling
        chartConfig.data.datasets = chartConfig.data.datasets.map((originalDataset) => {
          // Initialize data array with zeros matching labels length
          const data = new Array(labels.length).fill(0);

          // Map dimension values to the correct label positions
          dimensionKeys.forEach((dimensionKey) => {
            const value = averageResults[dimensionKey];
            if (value !== undefined) {
              const labelIndex = labels.indexOf(dimensionKey);
              if (labelIndex !== -1) {
                data[labelIndex] = value;
              }
            }
          });

          return {
            ...originalDataset, // Keep ALL original properties (colors, styling, etc.)
            data, // Update only the data
            label: `${this.$t('TeamResultDashboardComponent.teamAverage')} (${this.getAssessmentName(assessmentName)})`, // Use translated assessment name
            order: 1, // Show behind standard deviation
          };
        });

        // Add standard deviation dataset if we have standard deviation data
        // show only for bar chart
        if (standardDeviationResults && Object.keys(standardDeviationResults).length > 0 && chartConfig.type === 'bar') {
          // Map standardDeviationResults to the correct label positions
          const standardDeviationData = new Array(labels.length).fill(0);
          dimensionKeys.forEach((dimensionKey) => {
            const value = standardDeviationResults[dimensionKey];
            if (value !== undefined) {
              const labelIndex = labels.indexOf(dimensionKey);
              if (labelIndex !== -1) {
                standardDeviationData[labelIndex] = value;
              }
            }
          });

          // Main datasets use default x and y axes (no IDs needed)
          // Add standard deviation with hidden second x-axis for overlap
          chartConfig.data.datasets.push({
            label: this.$t('TeamResultDashboardComponent.standardDeviation'),
            data: standardDeviationData,
            type: 'bar',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            borderColor: '#000000',
            borderWidth: 1,
            order: -1, // Show in front of main data
            xAxisID: 'bar-x-axis2', // Use hidden second x-axis for offset
            // Use default y axis (no yAxisID specified)
          });
        }

        // Add dimension average as a separate bar when enabled
        const avgData = chartConfig.averageData;
        if (chartConfig.type === 'bar' && avgData?.enabled && avgData?.dimensions?.length) {
          const avgDimensions = avgData.dimensions;
          const avgWeights = avgData.weights || {};
          let weightedSum = 0;
          let totalWeight = 0;
          avgDimensions.forEach((dim) => {
            const value = averageResults[dim];
            if (value !== undefined && value !== null) {
              const weight = avgWeights[dim] ?? 1;
              weightedSum += Number(value) * weight;
              totalWeight += weight;
            }
          });
          if (totalWeight > 0) {
            const roundedAverage = roundToTwoDecimals(weightedSum / totalWeight);
            const locale = this.$i18n?.locale || 'en';
            const averageLabelText = locale === 'de' ? (avgData.title?.de) : (avgData.title?.en) || 'Average';
            // Add "Average" as a new category so it appears as a separate bar
            chartConfig.data.labels = [...(chartConfig.data.labels || []), averageLabelText];
            // Extend all existing datasets with null so they don't show a bar in the Average column
            chartConfig.data.datasets.forEach((ds) => {
              if (Array.isArray(ds.data)) ds.data.push(null);
            });
            chartConfig.data.datasets.push({
              label: averageLabelText,
              data: [...Array(labels.length).fill(null), roundedAverage],
              borderColor: avgData.borderColor?.startsWith('#') ? avgData.borderColor : `#${avgData.borderColor || '999'}`,
              backgroundColor: avgData.backgroundColor?.startsWith('#') ? avgData.backgroundColor : `#${avgData.backgroundColor || '999'}`,
              borderWidth: 2,
              order: 1,
            });
          }
        }
      }

      // Set y-axis scale with min 0 and max 5 for all bar charts
      if (chartConfig.type === 'bar') {
        // Check if standard deviation is present (has bar-x-axis2)
        const hasStandardDeviation = chartConfig.data.datasets.some(
          (dataset) => dataset.xAxisID === 'bar-x-axis2',
        );

        if (hasStandardDeviation) {
          // Configure axes for overlapping standard deviation
          // Keep original x and y axes, add hidden second x-axis for offset
          chartConfig.options = {
            ...chartConfig.options,
            scales: {
              ...chartConfig.options?.scales,
              // Keep original x axis (default, no ID)
              x: {
                stacked: true,
                ...chartConfig.options?.scales?.x,
              },
              // Hidden second x-axis for standard deviation offset
              'bar-x-axis2': {
                display: false,
                stacked: true,
                id: 'bar-x-axis2',
                position: 'bottom',
                barThickness: 70,
                type: 'category',
                categoryPercentage: 0.8,
                barPercentage: 0.9,
                offset: true,
              },
              // Keep original y axis (default, no ID)
              y: {
                stacked: false,
                max: this.config.chartScale.max,
                min: this.config.chartScale.min,
                ticks: {
                  beginAtZero: true,
                },
                ...chartConfig.options?.scales?.y,
              },
            },
          };
        }
        // Standard configuration when no standard deviation
        if (chartConfig.options && chartConfig.options.scales) {
          if (chartConfig.options.scales.y) {
            chartConfig.options.scales.y.max = this.config.chartScale.max;
            chartConfig.options.scales.y.min = this.config.chartScale.min;
            chartConfig.options.scales.y.stacked = false; // Y-axis should never be stacked
          } else {
            chartConfig.options.scales.y = {
              max: this.config.chartScale.max,
              min: this.config.chartScale.min,
              stacked: false, // Y-axis should never be stacked
            };
          }
          // Configure X-axis stacking
          if (chartConfig.options.scales.x) {
            chartConfig.options.scales.x.stacked = true;
          } else {
            chartConfig.options.scales.x = {
              stacked: true,
            };
          }
        } else {
          chartConfig.options = {
            ...chartConfig.options,
            scales: {
              ...chartConfig.options?.scales,
              y: {
                max: this.config.chartScale.max,
                min: this.config.chartScale.min,
                stacked: false,
              },
              x: {
                stacked: true,
              },
            },
          };
        }
      }

      // Set y-axis scale for line charts (Likert: min 0, max 5) so LineChartComponent useLikertScale works
      if (chartConfig.type === 'line') {
        if (chartConfig.options && chartConfig.options.scales) {
          chartConfig.options.scales.y = {
            ...chartConfig.options.scales.y,
            min: this.config.chartScale.min,
            max: this.config.chartScale.max,
          };
        } else {
          chartConfig.options = {
            ...chartConfig.options,
            scales: {
              ...chartConfig.options?.scales,
              y: {
                min: this.config.chartScale.min,
                max: this.config.chartScale.max,
              },
            },
          };
        }
      }

      // Native boxplot: ensure Likert y-bounds before updateChartData replaces data with quartile stats
      if (chartConfig.type === 'boxplot') {
        if (chartConfig.options && chartConfig.options.scales) {
          chartConfig.options.scales.y = {
            ...chartConfig.options.scales.y,
            min: this.config.chartScale.min ?? 0,
            max: this.config.chartScale.max ?? 5,
          };
        } else {
          chartConfig.options = {
            ...chartConfig.options,
            scales: {
              ...chartConfig.options?.scales,
              y: {
                min: this.config.chartScale.min ?? 0,
                max: this.config.chartScale.max ?? 5,
              },
            },
          };
        }
      }

      return chartConfig;
    },
  },
  async mounted() {
    try {
      this.loading = true;

      // Clear all chart configs and original configs to ensure fresh start
      this.originalChartConfigs = {};
      this.clearAllChartConfigs();

      // Reset team management store first to ensure clean state
      this.$store.commit('resetTeamManagement');

      // Get teams from team management store
      await this.$store.dispatch('getLeadedTeams');

      // If no teams, show no teams message
      if (!this.leadedTeams || this.leadedTeams.length === 0) {
        this.loading = false;
        return;
      }

      // Get team members from all teams
      const allTeamMembers = this.teamsForClientDashboard.reduce(
        (members, team) => [...members, ...(team.members || [])],
        [],
      );

      // Remove duplicates
      const uniqueTeamMembers = [...new Set(allTeamMembers)];

      // Fetch team assessment results
      const rawAssessments = await this.getTeamAssessmentsResults({
        teamMembers: uniqueTeamMembers,
        status: 'submitted',
      });

      // Store raw assessments for line charts
      this.rawTeamAssessments = rawAssessments || [];

      // Fetch other required data
      await this.fetchAssessments();
      await this.fetchCharts();
      await this.fetchDimensions();

      // Process the data after all API calls are complete
      const processedAssessments = await this.processTeamAssessmentData();

      // Update the store with processed data
      this.$store.commit('assessments/SET_TEAM_ASSESSMENTS', processedAssessments);

      // Set initial selected tab if we have assessments
      if (processedAssessments.length > 0) {
        this.selectedTab = processedAssessments[0].assessmentId;

        // Update chart data for the initial tab (especially important for KSS charts)
        await this.$nextTick();
        await this.updateChartData(this.selectedTab);
      }

      this.loading = false;
    } catch (error) {
      this.error = 'Failed to load team assessment results';
      this.loading = false;
    }
  },
};
</script>

<style scoped>
/* Add any specific styles for the team results dashboard here */
</style>
